import { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contactsAPI, companiesAPI, notesAPI, meetingsAPI, ticketsAPI, dealsAPI, tasksAPI, productsAPI, attachmentsAPI, teamAPI, subscriptionsAPI, itemAPI, portalAPI, engagementAPI, aiAssistantAPI, segmentsAPI, productLinesAPI, emailIntegrationAPI, emailTemplatesAPI, playbookNotesAPI, salesPlaybookAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { EngagementScoreBadge, AutomationTags, EngagementCard } from '../components/EngagementScoreBadge';
import { AIProcessingIndicator } from '../components/AIProcessingIndicator';
import { 
  TasksTab, NotesTab, EmailsTab, CallsTab, MeetingsTab, FilesTab, AITasksTab, CampaignsTab,
  CreateTaskDialog, CreateNoteDialog, CreateTicketDialog, LogEmailDialog, LogCallDialog, 
  InlineTicketDialog, InlineDealDialog, InlinePaymentDialog, InlineSubscriptionDialog, 
  MeetingDialog, PortalAccessDialog, CustomerStatusDialog, SegmentsDialog, EditContactDialog,
  // Detail Dialogs
  ActivityDetailDialog, EmailDetailDialog, CallDetailDialog, PaymentDetailDialog,
  NoteDetailDialog, MeetingDetailDialog, SubscriptionDetailDialog, DealDetailDialog,
  // Create Dialogs
  SimpleTicketDialog, SimpleSubscriptionDialog, AttachmentDialog, SimplePaymentDialog,
  CompanyDialog, SimpleDealDialog
} from './contact-profile';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../components/ui/collapsible';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, ArrowRight, User, Mail, Phone, Building, Building2, MapPin, Edit, Trash2, Plus,
  MessageSquare, Send, PhoneCall, Calendar as CalendarIcon, CheckSquare,
  TicketIcon, CreditCard, Receipt, FileText, Paperclip, PlayCircle, Workflow,
  Clock, DollarSign, Pin, Video, ExternalLink, MoreHorizontal, ChevronDown,
  ChevronRight, Search, Filter, Settings, HandshakeIcon, Package, RefreshCw, Eye,
  Upload, AlertCircle, Users, Check, Sparkles, TrendingUp, Target, Lightbulb, ListChecks,
  Archive, History, X, Loader2, Key, Bot, PhoneOutgoing, Image as ImageIcon, FolderOpen,
  AlertTriangle, Crown, Shield, Star, ThumbsUp, UserX, Flag, Link2, Megaphone, Download, CheckCircle
} from 'lucide-react';
import { QuickTaskDialog } from '../components/QuickTaskDialog';
import { PlaybookNotesSection } from '../components/PlaybookNotesSection';

// Customer Status Banner Component
const CustomerStatusBanner = ({ status, statusNote, onEdit }) => {
  const statusConfig = {
    vip: { 
      icon: Crown, 
      label: 'VIP Customer', 
      bg: 'bg-amber-50 border-amber-200', 
      text: 'text-amber-800',
      iconColor: 'text-amber-600'
    },
    good: { 
      icon: ThumbsUp, 
      label: 'Good Standing', 
      bg: 'bg-green-50 border-green-200', 
      text: 'text-green-800',
      iconColor: 'text-green-600'
    },
    hostile: { 
      icon: AlertTriangle, 
      label: 'Hostile Customer', 
      bg: 'bg-red-50 border-red-200', 
      text: 'text-red-800',
      iconColor: 'text-red-600'
    },
    escalation: { 
      icon: Flag, 
      label: 'Management Escalation', 
      bg: 'bg-orange-50 border-orange-200', 
      text: 'text-orange-800',
      iconColor: 'text-orange-600'
    },
    at_risk: { 
      icon: AlertCircle, 
      label: 'At Risk', 
      bg: 'bg-yellow-50 border-yellow-200', 
      text: 'text-yellow-800',
      iconColor: 'text-yellow-600'
    },
    new: { 
      icon: Star, 
      label: 'New Customer', 
      bg: 'bg-blue-50 border-blue-200', 
      text: 'text-blue-800',
      iconColor: 'text-blue-600'
    },
    churned: { 
      icon: UserX, 
      label: 'Churned', 
      bg: 'bg-slate-100 border-slate-300', 
      text: 'text-slate-700',
      iconColor: 'text-slate-500'
    },
  };

  const config = statusConfig[status] || statusConfig.good;
  const StatusIcon = config.icon;

  return (
    <div className={`mb-4 p-3 ${config.bg} rounded-lg border flex items-center justify-between`} data-testid="customer-status-banner">
      <div className="flex items-center gap-3">
        <StatusIcon className={`h-5 w-5 ${config.iconColor}`} />
        <div>
          <span className={`font-semibold ${config.text}`}>{config.label}</span>
          {statusNote && (
            <p className={`text-sm ${config.text} opacity-80`}>{statusNote}</p>
          )}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onEdit} className={config.text}>
        <Edit className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const ContactProfilePage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [profileSummary, setProfileSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [contactProducts, setContactProducts] = useState([]); // Products assigned to this contact
  
  // Tab data
  const [notes, setNotes] = useState([]);
  const [emails, setEmails] = useState([]);
  const [calls, setCalls] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [warrantyClaims, setWarrantyClaims] = useState([]);
  const [portalFiles, setPortalFiles] = useState([]);
  const [portalChats, setPortalChats] = useState([]);
  const [portalWarranties, setPortalWarranties] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contactCampaigns, setContactCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  
  // Sidebar collapse states
  const [aboutOpen, setAboutOpen] = useState(true);
  const [companiesOpen, setCompaniesOpen] = useState(true);
  const [dealsOpen, setDealsOpen] = useState(true);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [subscriptionsOpen, setSubscriptionsOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [contactProductsOpen, setContactProductsOpen] = useState(true); // Products sidebar
  
  // Filter states
  const [activityFilter, setActivityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [noteDialog, setNoteDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [callDialog, setCallDialog] = useState(false);
  const [meetingDialog, setMeetingDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [ticketDialog, setTicketDialog] = useState(false);
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [attachmentDialog, setAttachmentDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [companyDialog, setCompanyDialog] = useState(false);
  const [dealDialog, setDealDialog] = useState(false);
  const [activityDetailDialog, setActivityDetailDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // View/Edit detail dialogs
  const [noteDetailDialog, setNoteDetailDialog] = useState(false);
  const [taskDetailDialog, setTaskDetailDialog] = useState(false);
  const [ticketDetailDialog, setTicketDetailDialog] = useState(false);
  const [dealDetailDialog, setDealDetailDialog] = useState(false);
  const [subscriptionDetailDialog, setSubscriptionDetailDialog] = useState(false);
  const [emailDetailDialog, setEmailDetailDialog] = useState(false);
  const [callDetailDialog, setCallDetailDialog] = useState(false);
  const [meetingDetailDialog, setMeetingDetailDialog] = useState(false);
  
  // QuickTaskDialog state - for creating tasks from entities
  const [quickTaskDialog, setQuickTaskDialog] = useState(false);
  const [quickTaskEntity, setQuickTaskEntity] = useState({ type: '', id: '', name: '' });
  
  // Selected items for viewing/editing
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskAIHistory, setSelectedTaskAIHistory] = useState(null);
  const [loadingTaskAIHistory, setLoadingTaskAIHistory] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailDialog, setPaymentDetailDialog] = useState(false);
  const [selectedContactProduct, setSelectedContactProduct] = useState(null);
  const [contactProductDialog, setContactProductDialog] = useState(false);
  const [contactProductDetailDialog, setContactProductDetailDialog] = useState(false);
  
  // Comments thread states
  const [itemComments, setItemComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Follow-up states
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupAssignee, setFollowupAssignee] = useState('');
  const [followupMessage, setFollowupMessage] = useState('');
  
  // Portal access dialog
  const [portalAccessDialog, setPortalAccessDialog] = useState(false);
  const [portalPassword, setPortalPassword] = useState('');
  const [portalCredentials, setPortalCredentials] = useState(null);
  const [existingPortalAccess, setExistingPortalAccess] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Edit contact dialog
  const [editContactDialog, setEditContactDialog] = useState(false);
  const [editContactForm, setEditContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    status: 'active',
    source: '',
    notes: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  
  // Segments dialog
  const [segmentsDialog, setSegmentsDialog] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [editingSegment, setEditingSegment] = useState(null);
  const [newSegmentForm, setNewSegmentForm] = useState({ label: '', color: 'bg-blue-100 text-blue-700' });
  const [isCreatingSegment, setIsCreatingSegment] = useState(false);
  
  // AI Sales Assistant
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsHistory, setAiInsightsHistory] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showArchivedInsights, setShowArchivedInsights] = useState(false);
  
  // Edit modes
  const [editMode, setEditMode] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  
  // Follow-up task states for Email/Call logging
  const [createFollowUpTask, setCreateFollowUpTask] = useState(false);
  const [followUpTaskDetails, setFollowUpTaskDetails] = useState({ 
    title: '', 
    due_date: '', 
    assigned_to: '', 
    assigned_type: 'user' // 'user' or 'ai_agent'
  });

  // Customer Status Banner
  const [customerStatus, setCustomerStatus] = useState(null); // vip, hostile, good, escalation, at_risk, new
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusNote, setStatusNote] = useState('');

  // Inline Create Dialogs (don't navigate away)
  const [showInlineTicketDialog, setShowInlineTicketDialog] = useState(false);
  const [showInlineDealDialog, setShowInlineDealDialog] = useState(false);
  const [showInlinePaymentDialog, setShowInlinePaymentDialog] = useState(false);
  const [showInlineSubscriptionDialog, setShowInlineSubscriptionDialog] = useState(false);
  
  // Deal form state
  const [dealForm, setDealForm] = useState({
    name: '',
    value: 0,
    stage: 'lead',
    expected_close_date: '',
    probability: 20,
    notes: ''
  });
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'credit_card',
    description: '',
    reference_number: ''
  });
  
  // Form states
  const [noteForm, setNoteForm] = useState({ content: '', is_pinned: false });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', direction: 'outbound' });
  const [callForm, setCallForm] = useState({ direction: 'outbound', duration_minutes: 0, outcome: 'connected', notes: '' });
  const [meetingForm, setMeetingForm] = useState({ title: '', description: '', start_time: null, end_time: null, location: '', meeting_link: '', attendees: [] });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', priority: 'medium', assigned_to: '', assigned_ai_agent_id: '' });
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', type: 'support', priority: 'medium', status: 'open', assigned_to: '' });
  const [subscriptionForm, setSubscriptionForm] = useState({ plan_name: '', plan_price: 0, billing_cycle: 'monthly', start_date: '', status: 'active' });
  const [attachmentForm, setAttachmentForm] = useState({ file: null, description: '' });
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [companyForm, setCompanyForm] = useState({ name: '', industry: '', website: '', phone: '', address: '' });
  const [dealFormOld, setDealFormOld] = useState({ title: '', value: 0, stage: 'prospecting', expected_close_date: '', line_items: [] });
  const [newDealLineItem, setNewDealLineItem] = useState({ product_id: '', quantity: 1, discount_percent: 0 });
  const [contactProductForm, setContactProductForm] = useState({ 
    product_id: '', 
    serial_numbers: [''], // Array of serial numbers
    purchase_date: '', 
    purchase_price: '', 
    discount_percent: 0, 
    discount_amount: 0, 
    notes: '' 
  });
  
  // Email compose state
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [composeEmailForm, setComposeEmailForm] = useState({ subject: '', body: '', cc: '', bcc: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);

  useEffect(() => {
    fetchContactData();
    fetchTeamUsers();
    fetchAiAgents();
    fetchSegments();
    checkEmailConnection();
  }, [contactId]);

  const checkEmailConnection = async () => {
    try {
      const [settingsRes, templatesRes] = await Promise.allSettled([
        emailIntegrationAPI.getSettings(),
        emailTemplatesAPI.getAll()
      ]);
      
      if (settingsRes.status === 'fulfilled') {
        setEmailConnected(settingsRes.value.data.connected);
      }
      if (templatesRes.status === 'fulfilled') {
        setEmailTemplates(templatesRes.value.data || []);
      }
    } catch (error) {
      setEmailConnected(false);
    }
  };

  const applyEmailTemplate = (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      // Replace variables with contact data
      let body = template.body || '';
      let subject = template.subject || '';
      
      const replacements = {
        '{{first_name}}': contact?.first_name || '',
        '{{last_name}}': contact?.last_name || '',
        '{{full_name}}': `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim(),
        '{{email}}': contact?.email || '',
        '{{company}}': company?.name || '',
      };
      
      Object.entries(replacements).forEach(([key, value]) => {
        body = body.replace(new RegExp(key, 'g'), value);
        subject = subject.replace(new RegExp(key, 'g'), value);
      });
      
      setComposeEmailForm(prev => ({
        ...prev,
        subject,
        body
      }));
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await segmentsAPI.getAll();
      setCustomerSegments(res.data.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments');
    }
  };

  const fetchTeamUsers = async () => {
    try {
      const res = await teamAPI.getUsers();
      setTeamUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch team users');
    }
  };

  const fetchAiAgents = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-workforce/agents?status=active`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiAgents(data);
      }
    } catch (error) {
      console.error('Failed to fetch AI agents');
    }
  };

  const fetchContactData = async () => {
    try {
      const [contactRes, profileRes, timelineRes] = await Promise.all([
        contactsAPI.getById(contactId),
        contactsAPI.getProfile(contactId),
        contactsAPI.getTimeline(contactId)
      ]);
      
      setContact(contactRes.data);
      setProfileSummary(profileRes.data);
      setTimeline(timelineRes.data);
      
      if (contactRes.data.company_id) {
        try {
          const companyRes = await companiesAPI.getById(contactRes.data.company_id);
          setCompany(companyRes.data);
        } catch (e) {
          // Company not found, continue without it
        }
      }
      
      fetchTabData();
    } catch (error) {
      toast.error('Failed to fetch contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async () => {
    try {
      const [notesRes, emailsRes, callsRes, meetingsRes, tasksRes, ticketsRes, warrantyClaimsRes,
             subsRes, paymentsRes, invoicesRes, attachRes, dealsRes, productsRes, contactProductsRes, aiHistoryRes, campaignsRes,
             portalFilesRes, portalChatsRes, portalWarrantiesRes] = await Promise.all([
        contactsAPI.getNotes(contactId),
        contactsAPI.getEmails(contactId),
        contactsAPI.getCalls(contactId),
        contactsAPI.getMeetings(contactId),
        contactsAPI.getTasks(contactId),
        contactsAPI.getTickets(contactId),
        contactsAPI.getWarrantyClaims(contactId).catch(() => ({ data: [] })),
        contactsAPI.getSubscriptions(contactId),
        contactsAPI.getPayments(contactId),
        contactsAPI.getInvoices(contactId),
        contactsAPI.getAttachments(contactId),
        contactsAPI.getDeals(contactId),
        productsAPI.getAll(),
        contactsAPI.getProducts(contactId),
        aiAssistantAPI.getInsightsHistory(contactId, 10, showArchivedInsights),
        contactsAPI.getCampaigns(contactId).catch(() => ({ data: { campaigns: [], stats: {} } })),
        contactsAPI.getPortalFiles(contactId).catch(() => ({ data: [] })),
        contactsAPI.getPortalChats(contactId).catch(() => ({ data: [] })),
        contactsAPI.getPortalWarranties(contactId).catch(() => ({ data: [] }))
      ]);
      
      setNotes(notesRes.data);
      setEmails(emailsRes.data);
      setCalls(callsRes.data);
      setMeetings(meetingsRes.data);
      setTasks(tasksRes.data);
      setTickets(ticketsRes.data);
      setWarrantyClaims(warrantyClaimsRes.data || []);
      setPortalFiles(portalFilesRes.data || []);
      setPortalChats(portalChatsRes.data || []);
      setPortalWarranties(portalWarrantiesRes.data || []);
      setSubscriptions(subsRes.data);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
      setAttachments(attachRes.data);
      setDeals(dealsRes.data);
      setProducts((productsRes.data || []).filter(p => p.is_active));
      setContactProducts(contactProductsRes.data || []);
      setAiInsightsHistory(aiHistoryRes.data || []);
      setContactCampaigns(campaignsRes.data?.campaigns || []);
      setCampaignStats(campaignsRes.data?.stats || {});
      
      // Set latest insight as current if available
      if (aiHistoryRes.data && aiHistoryRes.data.length > 0) {
        const latest = aiHistoryRes.data[0];
        setAiInsights({
          ...latest.insights,
          generated_at: latest.generated_at,
          insight_id: latest.id
        });
      }
    } catch (error) {
      console.error('Error fetching tab data:', error);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await contactsAPI.createNote(contactId, { ...noteForm, contact_id: contactId });
      toast.success('Note added');
      setNoteDialog(false);
      setNoteForm({ content: '', is_pinned: false });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleCreateEmail = async (e) => {
    e.preventDefault();
    try {
      // Log the email
      await contactsAPI.createEmail(contactId, { ...emailForm, contact_id: contactId });
      
      // Create follow-up task if requested
      if (createFollowUpTask && followUpTaskDetails.title) {
        // Find the assigned name based on type
        let assignedToName = '';
        if (followUpTaskDetails.assigned_type === 'ai_agent') {
          const agent = aiAgents.find(a => a.agent_type === followUpTaskDetails.assigned_to);
          assignedToName = agent?.display_name || followUpTaskDetails.assigned_to;
        } else {
          const member = teamUsers.find(m => m.id === followUpTaskDetails.assigned_to);
          assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
        }
        
        const taskData = {
          title: followUpTaskDetails.title,
          description: `Follow-up for email: ${emailForm.subject}`,
          priority: 'medium',
          status: 'pending',
          due_date: followUpTaskDetails.due_date ? new Date(followUpTaskDetails.due_date).toISOString() : null,
          assigned_to: followUpTaskDetails.assigned_to || null,
          assigned_to_type: followUpTaskDetails.assigned_type,
          assigned_to_name: assignedToName,
          is_follow_up: true
        };
        await contactsAPI.createTask(contactId, taskData);
        toast.success('Email logged with follow-up task created');
      } else {
        toast.success('Email logged');
      }
      
      setEmailDialog(false);
      setEmailForm({ subject: '', body: '', direction: 'outbound' });
      setCreateFollowUpTask(false);
      setFollowUpTaskDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to log email');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!contact?.email) {
      toast.error('Contact has no email address');
      return;
    }
    setSendingEmail(true);
    try {
      await emailIntegrationAPI.sendEmail({
        to_email: contact.email,
        subject: composeEmailForm.subject,
        body: composeEmailForm.body,
        cc: composeEmailForm.cc ? composeEmailForm.cc.split(',').map(e => e.trim()) : [],
        bcc: composeEmailForm.bcc ? composeEmailForm.bcc.split(',').map(e => e.trim()) : [],
        contact_id: contactId
      });
      toast.success('Email sent successfully!');
      setIsComposeEmailOpen(false);
      setComposeEmailForm({ subject: '', body: '', cc: '', bcc: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateCall = async (e) => {
    e.preventDefault();
    try {
      // Log the call
      await contactsAPI.createCall(contactId, { 
        ...callForm, 
        contact_id: contactId,
        duration_minutes: parseInt(callForm.duration_minutes) || 0
      });
      
      // Create follow-up task if requested
      if (createFollowUpTask && followUpTaskDetails.title) {
        // Find the assigned name based on type
        let assignedToName = '';
        if (followUpTaskDetails.assigned_type === 'ai_agent') {
          const agent = aiAgents.find(a => a.agent_type === followUpTaskDetails.assigned_to);
          assignedToName = agent?.display_name || followUpTaskDetails.assigned_to;
        } else {
          const member = teamUsers.find(m => m.id === followUpTaskDetails.assigned_to);
          assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
        }
        
        const taskData = {
          title: followUpTaskDetails.title,
          description: `Follow-up for call: ${callForm.outcome} - ${callForm.notes?.substring(0, 100) || 'No notes'}`,
          priority: 'medium',
          status: 'pending',
          due_date: followUpTaskDetails.due_date ? new Date(followUpTaskDetails.due_date).toISOString() : null,
          assigned_to: followUpTaskDetails.assigned_to || null,
          assigned_to_type: followUpTaskDetails.assigned_type,
          assigned_to_name: assignedToName,
          is_follow_up: true
        };
        await contactsAPI.createTask(contactId, taskData);
        toast.success('Call logged with follow-up task created');
      } else {
        toast.success('Call logged');
      }
      
      setCallDialog(false);
      setCallForm({ direction: 'outbound', duration_minutes: 0, outcome: 'connected', notes: '' });
      setCreateFollowUpTask(false);
      setFollowUpTaskDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to log call');
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    if (!meetingForm.start_time || !meetingForm.end_time) {
      toast.error('Please select start and end time');
      return;
    }
    try {
      await contactsAPI.createMeeting(contactId, {
        ...meetingForm,
        contact_id: contactId,
        start_time: new Date(meetingForm.start_time).toISOString(),
        end_time: new Date(meetingForm.end_time).toISOString(),
        attendees: meetingForm.attendees || []
      });
      toast.success('Meeting scheduled' + (meetingForm.attendees.length > 0 ? ` with ${meetingForm.attendees.length} team member(s)` : ''));
      setMeetingDialog(false);
      setMeetingForm({ title: '', description: '', start_time: null, end_time: null, location: '', meeting_link: '', attendees: [] });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create meeting');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // Determine assignment type and name
      let assignedToType = 'user';
      let assignedToName = '';
      let assignedTo = taskForm.assigned_to;
      
      if (taskForm.assigned_ai_agent_id) {
        assignedToType = 'ai_agent';
        assignedTo = taskForm.assigned_ai_agent_id;
        const agent = aiAgents.find(a => a.agent_type === taskForm.assigned_ai_agent_id);
        assignedToName = agent?.display_name || taskForm.assigned_ai_agent_id;
      } else if (taskForm.assigned_to) {
        const member = teamUsers.find(m => m.id === taskForm.assigned_to);
        assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
      }
      
      const taskData = {
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        status: 'pending',
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
        assigned_to: assignedTo || null,
        assigned_to_type: assignedToType,
        assigned_to_name: assignedToName || null
      };
      
      await contactsAPI.createTask(contactId, taskData);
      toast.success('Task created' + (assignedTo ? ' and assigned' : ''));
      setTaskDialog(false);
      setTaskForm({ title: '', description: '', due_date: '', priority: 'medium', assigned_to: '', assigned_ai_agent_id: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      toast.success('Note deleted');
      fetchTabData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject) {
      toast.error('Please enter a subject');
      return;
    }
    try {
      await contactsAPI.createTicket(contactId, ticketForm);
      toast.success('Ticket created');
      setTicketDialog(false);
      setTicketForm({ subject: '', description: '', type: 'support', priority: 'medium' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      console.error('Ticket creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    if (!subscriptionForm.start_date) {
      toast.error('Please select a start date');
      return;
    }
    try {
      await contactsAPI.createSubscription(contactId, {
        ...subscriptionForm,
        start_date: new Date(subscriptionForm.start_date).toISOString()
      });
      toast.success('Subscription created');
      setSubscriptionDialog(false);
      setShowInlineSubscriptionDialog(false);
      setSubscriptionForm({ plan_name: '', plan_price: 0, billing_cycle: 'monthly', start_date: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create subscription');
    }
  };

  // Customer Status Handler
  const handleSetStatus = async (newStatus, note = '') => {
    try {
      await contactsAPI.update(contactId, { 
        customer_status: newStatus,
        status_note: note 
      });
      setContact(prev => ({ ...prev, customer_status: newStatus, status_note: note }));
      setShowStatusDialog(false);
      setStatusNote('');
      toast.success(`Customer marked as ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to update customer status');
    }
  };

  // Inline Ticket Create
  const handleCreateInlineTicket = async (e) => {
    e.preventDefault();
    try {
      await contactsAPI.createTicket(contactId, ticketForm);
      toast.success('Ticket created');
      setShowInlineTicketDialog(false);
      setTicketForm({ subject: '', description: '', type: 'support', priority: 'medium', status: 'open', assigned_to: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  // Inline Deal Create
  const handleCreateInlineDeal = async (e) => {
    e.preventDefault();
    try {
      await dealsAPI.create({
        ...dealForm,
        contact_id: contactId,
        company_id: contact?.company_id,
        expected_close_date: dealForm.expected_close_date ? new Date(dealForm.expected_close_date).toISOString() : null
      });
      toast.success('Deal created');
      setShowInlineDealDialog(false);
      setDealForm({ name: '', value: 0, stage: 'lead', expected_close_date: '', probability: 20, notes: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  // Inline Payment Record
  const handleCreateInlinePayment = async (e) => {
    e.preventDefault();
    try {
      await contactsAPI.createPayment(contactId, {
        ...paymentForm,
        payment_date: new Date().toISOString(),
        status: 'completed'
      });
      toast.success('Payment recorded');
      setShowInlinePaymentDialog(false);
      setPaymentForm({ amount: 0, payment_method: 'credit_card', description: '', reference_number: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleCreateAttachment = async (e) => {
    e.preventDefault();
    if (!attachmentForm.file) {
      toast.error('Please select a file to upload');
      return;
    }
    setAttachmentUploading(true);
    try {
      await attachmentsAPI.upload(contactId, attachmentForm.file, attachmentForm.description);
      toast.success('File uploaded successfully');
      setAttachmentDialog(false);
      setAttachmentForm({ file: null, description: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchTabData();
      fetchContactData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachmentForm(prev => ({ ...prev, file }));
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await contactsAPI.createPayment(contactId, paymentForm);
      toast.success('Payment recorded');
      setPaymentDialog(false);
      setPaymentForm({ amount: 0, method: 'credit_card', description: '' });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Contact Product handlers
  const handleAddContactProduct = async (e) => {
    e.preventDefault();
    if (!contactProductForm.product_id) {
      toast.error('Please select a product');
      return;
    }
    try {
      const payload = {
        ...contactProductForm,
        serial_numbers: contactProductForm.serial_numbers.filter(sn => sn.trim()), // Filter out empty strings
        purchase_price: contactProductForm.purchase_price ? parseFloat(contactProductForm.purchase_price) : null,
        discount_percent: parseFloat(contactProductForm.discount_percent) || 0,
        discount_amount: parseFloat(contactProductForm.discount_amount) || 0,
        purchase_date: contactProductForm.purchase_date ? new Date(contactProductForm.purchase_date).toISOString() : null
      };
      await contactsAPI.addProduct(contactId, payload);
      toast.success('Product assigned to contact');
      setContactProductDialog(false);
      setContactProductForm({ product_id: '', serial_numbers: [''], purchase_date: '', purchase_price: '', discount_percent: 0, discount_amount: 0, notes: '' });
      fetchTabData();
      
      // Trigger engagement campaigns for product_purchase
      try {
        await engagementAPI.trigger('product_purchase', contactId, contactProductForm.product_id, 'product');
      } catch (err) {
        // Silently fail - engagement triggers are optional
        console.log('Engagement trigger skipped');
      }
    } catch (error) {
      toast.error('Failed to assign product');
    }
  };

  const handleUpdateContactProduct = async () => {
    if (!selectedContactProduct) return;
    try {
      const payload = {
        serial_number: selectedContactProduct.serial_number,
        purchase_price: selectedContactProduct.purchase_price ? parseFloat(selectedContactProduct.purchase_price) : null,
        discount_percent: parseFloat(selectedContactProduct.discount_percent) || 0,
        discount_amount: parseFloat(selectedContactProduct.discount_amount) || 0,
        notes: selectedContactProduct.notes,
        status: selectedContactProduct.status
      };
      await contactsAPI.updateProduct(contactId, selectedContactProduct.id, payload);
      toast.success('Product updated');
      setContactProductDetailDialog(false);
      fetchTabData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDeleteContactProduct = async (productRecordId) => {
    if (!window.confirm('Remove this product from contact?')) return;
    try {
      await contactsAPI.deleteProduct(contactId, productRecordId);
      toast.success('Product removed');
      setContactProductDetailDialog(false);
      fetchTabData();
    } catch (error) {
      toast.error('Failed to remove product');
    }
  };

  const openContactProductDetail = (cp) => {
    setSelectedContactProduct(cp);
    setContactProductDetailDialog(true);
  };

  // Edit Contact handlers
  const openEditContactDialog = () => {
    if (contact) {
      setEditContactForm({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        job_title: contact.job_title || '',
        status: contact.status || 'active',
        source: contact.source || '',
        notes: contact.notes || ''
      });
      setEditContactDialog(true);
    }
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!editContactForm.first_name || !editContactForm.email) {
      toast.error('First name and email are required');
      return;
    }
    setSavingContact(true);
    try {
      await contactsAPI.update(contactId, editContactForm);
      toast.success('Contact updated successfully');
      setEditContactDialog(false);
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name) {
      toast.error('Please enter a company name');
      return;
    }
    try {
      const newCompany = await companiesAPI.create(companyForm);
      // Update contact with new company
      await contactsAPI.update(contactId, { company_id: newCompany.data.id });
      toast.success('Company created and linked');
      setCompanyDialog(false);
      setCompanyForm({ name: '', industry: '', website: '', phone: '', address: '' });
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    if (!dealForm.title) {
      toast.error('Please enter a deal title');
      return;
    }
    try {
      // Calculate total from line items
      const dealValue = dealForm.line_items.length > 0 
        ? dealForm.line_items.reduce((sum, item) => sum + item.total, 0)
        : parseFloat(dealForm.value) || 0;
        
      await dealsAPI.create({
        ...dealForm,
        value: dealValue,
        contact_id: contactId,
        expected_close_date: dealForm.expected_close_date ? new Date(dealForm.expected_close_date).toISOString() : null
      });
      toast.success('Deal created');
      setDealDialog(false);
      setDealForm({ title: '', value: 0, stage: 'prospecting', expected_close_date: '', line_items: [] });
      setNewDealLineItem({ product_id: '', quantity: 1, discount_percent: 0 });
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const handleAddDealLineItem = () => {
    if (!newDealLineItem.product_id) {
      toast.error('Please select a product');
      return;
    }
    const product = products.find(p => p.id === newDealLineItem.product_id);
    if (!product) return;

    const quantity = parseInt(newDealLineItem.quantity) || 1;
    const discount = parseFloat(newDealLineItem.discount_percent) || 0;
    const unitPrice = product.price;
    const total = unitPrice * quantity * (1 - discount / 100);

    const item = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      product_type: product.product_type || 'other',
      quantity,
      unit_price: unitPrice,
      discount_percent: discount,
      total
    };

    setDealForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, item]
    }));
    setNewDealLineItem({ product_id: '', quantity: 1, discount_percent: 0 });
  };

  const handleRemoveDealLineItem = (index) => {
    setDealForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const openActivityDetail = (activity) => {
    setSelectedActivity(activity);
    setActivityDetailDialog(true);
  };

  // Open item detail handlers
  const openNoteDetail = (note) => {
    setSelectedNote(note);
    setNoteForm({ content: note.content, is_pinned: note.is_pinned || false });
    setEditMode(false);
    setNoteDetailDialog(true);
  };

  const openTaskDetail = async (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
      priority: task.priority || 'medium',
      status: task.status || 'pending',
      assigned_to: task.assigned_to || ''
    });
    setEditMode(false);
    setTaskDetailDialog(true);
    
    // Fetch AI history if it's an AI task
    if (task.assigned_to_type === 'ai_agent') {
      setLoadingTaskAIHistory(true);
      try {
        const res = await tasksAPI.getAIHistory(task.id);
        setSelectedTaskAIHistory(res.data);
      } catch (error) {
        console.error('Failed to fetch AI history:', error);
        setSelectedTaskAIHistory(null);
      } finally {
        setLoadingTaskAIHistory(false);
      }
    } else {
      setSelectedTaskAIHistory(null);
    }
  };

  const openTicketDetail = (ticket) => {
    setSelectedTicket(ticket);
    setTicketForm({
      subject: ticket.subject || '',
      description: ticket.description || '',
      type: ticket.type || 'support',
      priority: ticket.priority || 'medium',
      status: ticket.status || 'open',
      assigned_to: ticket.assigned_to || ''
    });
    setEditMode(false);
    setTicketDetailDialog(true);
  };

  const openDealDetail = (deal) => {
    setSelectedDeal(deal);
    setDealForm({
      title: deal.title || '',
      value: deal.value || 0,
      stage: deal.stage || 'prospecting',
      expected_close_date: deal.expected_close_date ? new Date(deal.expected_close_date).toISOString().slice(0, 10) : '',
      line_items: deal.line_items || []
    });
    setEditMode(false);
    setDealDetailDialog(true);
  };

  const openSubscriptionDetail = (sub) => {
    setSelectedSubscription(sub);
    setSubscriptionForm({
      plan_name: sub.plan_name || '',
      plan_price: sub.plan_price || 0,
      billing_cycle: sub.billing_cycle || 'monthly',
      start_date: sub.start_date ? new Date(sub.start_date).toISOString().slice(0, 10) : '',
      status: sub.status || 'active'
    });
    setEditMode(false);
    setSubscriptionDetailDialog(true);
  };

  const openEmailDetail = (email) => {
    setSelectedEmail(email);
    setEmailDetailDialog(true);
  };

  const openCallDetail = (call) => {
    setSelectedCall(call);
    setCallDetailDialog(true);
  };

  const openMeetingDetail = (meeting) => {
    setSelectedMeeting(meeting);
    setMeetingForm({
      title: meeting.title || '',
      description: meeting.description || '',
      start_time: meeting.start_time ? new Date(meeting.start_time).toISOString().slice(0, 16) : '',
      end_time: meeting.end_time ? new Date(meeting.end_time).toISOString().slice(0, 16) : '',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      assigned_to: meeting.assigned_to || ''
    });
    setEditMode(false);
    setMeetingDetailDialog(true);
  };

  const openPaymentDetail = (payment) => {
    setSelectedPayment(payment);
    setPaymentDetailDialog(true);
  };

  // Update handlers
  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      await notesAPI.update(selectedNote.id, noteForm);
      toast.success('Note updated');
      setNoteDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksAPI.update(selectedTask.id, {
        ...taskForm,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null
      });
      toast.success('Task updated');
      setTaskDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleUpdateTicket = async (e) => {
    e.preventDefault();
    try {
      await ticketsAPI.update(selectedTicket.id, ticketForm);
      toast.success('Ticket updated');
      setTicketDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleUpdateDeal = async (e) => {
    e.preventDefault();
    try {
      await dealsAPI.update(selectedDeal.id, {
        ...dealForm,
        expected_close_date: dealForm.expected_close_date ? new Date(dealForm.expected_close_date).toISOString() : null
      });
      toast.success('Deal updated');
      setDealDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    try {
      await subscriptionsAPI.update(selectedSubscription.id, {
        ...subscriptionForm,
        start_date: subscriptionForm.start_date ? new Date(subscriptionForm.start_date).toISOString() : null
      });
      toast.success('Subscription updated');
      setSubscriptionDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleUpdateMeeting = async (e) => {
    e.preventDefault();
    try {
      await meetingsAPI.update(selectedMeeting.id, {
        ...meetingForm,
        start_time: meetingForm.start_time ? new Date(meetingForm.start_time).toISOString() : null,
        end_time: meetingForm.end_time ? new Date(meetingForm.end_time).toISOString() : null
      });
      toast.success('Meeting updated');
      setMeetingDetailDialog(false);
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to update meeting');
    }
  };

  // Comments and Follow-ups handlers
  const fetchItemComments = async (itemType, itemId) => {
    setLoadingComments(true);
    try {
      const res = await itemAPI.getComments(itemType, itemId);
      setItemComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments');
      setItemComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async (itemType, itemId) => {
    if (!newComment.trim()) return;
    try {
      await itemAPI.addComment(itemType, itemId, { 
        content: newComment, 
        is_internal: isInternalComment 
      });
      toast.success('Comment added');
      setNewComment('');
      setIsInternalComment(false);
      fetchItemComments(itemType, itemId);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleAddFollowup = async (itemType, itemId) => {
    if (!followupAssignee || !followupMessage.trim()) {
      toast.error('Please select assignee and enter a message');
      return;
    }
    try {
      await itemAPI.addFollowup(itemType, itemId, {
        assigned_to: followupAssignee,
        message: followupMessage
      });
      toast.success('Follow-up assigned');
      setShowFollowupForm(false);
      setFollowupAssignee('');
      setFollowupMessage('');
    } catch (error) {
      toast.error('Failed to create follow-up');
    }
  };

  const handleCloseItem = async (itemType, itemId) => {
    try {
      await itemAPI.closeItem(itemType, itemId);
      toast.success('Item closed');
      fetchTabData();
      fetchContactData();
      // Close the detail dialog
      setTicketDetailDialog(false);
      setDealDetailDialog(false);
      setTaskDetailDialog(false);
      setSubscriptionDetailDialog(false);
    } catch (error) {
      toast.error('Failed to close item');
    }
  };

  const handleReopenItem = async (itemType, itemId) => {
    try {
      await itemAPI.reopenItem(itemType, itemId);
      toast.success('Item reopened');
      fetchTabData();
      fetchContactData();
    } catch (error) {
      toast.error('Failed to reopen item');
    }
  };

  const openPortalAccessDialog = async () => {
    setPortalLoading(true);
    setPortalAccessDialog(true);
    try {
      const res = await portalAPI.checkAccess(contactId);
      if (res.data.has_access) {
        setExistingPortalAccess(res.data);
      } else {
        setExistingPortalAccess(null);
      }
    } catch (error) {
      setExistingPortalAccess(null);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCreatePortalAccess = async () => {
    if (!portalPassword || portalPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await portalAPI.createAccess({
        contact_id: contactId,
        email: contact.email,
        password: portalPassword
      });
      setPortalCredentials(res.data);
      toast.success('Portal access created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portal access');
    }
  };

  const handleResetPortalPassword = async () => {
    if (!portalPassword || portalPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await portalAPI.adminResetPassword({
        contact_id: contactId,
        new_password: portalPassword
      });
      setPortalCredentials(res.data);
      setShowResetPassword(false);
      toast.success('Portal password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getInitials = () => {
    if (!contact) return 'U';
    return `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase();
  };

  // Available customer segments
  const [customerSegments, setCustomerSegments] = useState([]);

  const handleSaveSegments = async () => {
    try {
      await contactsAPI.update(contactId, { segments: selectedSegments });
      setContact(prev => ({ ...prev, segments: selectedSegments }));
      toast.success('Segments updated');
      setSegmentsDialog(false);
    } catch (error) {
      toast.error('Failed to update segments');
    }
  };

  const toggleSegment = (segment) => {
    setSelectedSegments(prev => 
      prev.includes(segment) 
        ? prev.filter(s => s !== segment)
        : [...prev, segment]
    );
  };

  const handleCreateSegment = async () => {
    if (!newSegmentForm.label.trim()) {
      toast.error('Segment name is required');
      return;
    }
    try {
      const segmentValue = newSegmentForm.label.toLowerCase().replace(/\s+/g, '_');
      await segmentsAPI.create({
        value: segmentValue,
        label: newSegmentForm.label,
        color: newSegmentForm.color
      });
      toast.success('Segment created');
      setNewSegmentForm({ label: '', color: 'bg-blue-100 text-blue-700' });
      setIsCreatingSegment(false);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to create segment');
    }
  };

  const handleUpdateSegment = async (segmentId) => {
    if (!editingSegment?.label.trim()) {
      toast.error('Segment name is required');
      return;
    }
    try {
      await segmentsAPI.update(segmentId, {
        label: editingSegment.label,
        color: editingSegment.color
      });
      toast.success('Segment updated');
      setEditingSegment(null);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to update segment');
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!confirm('Are you sure you want to delete this segment? Contacts with this segment will have it removed.')) {
      return;
    }
    try {
      await segmentsAPI.delete(segmentId);
      toast.success('Segment deleted');
      // Remove from selected if it was selected
      setSelectedSegments(prev => prev.filter(s => s !== segmentId));
      fetchSegments();
    } catch (error) {
      toast.error('Failed to delete segment');
    }
  };

  const segmentColorOptions = [
    { value: 'bg-blue-100 text-blue-700', label: 'Blue' },
    { value: 'bg-emerald-100 text-emerald-700', label: 'Green' },
    { value: 'bg-purple-100 text-purple-700', label: 'Purple' },
    { value: 'bg-amber-100 text-amber-700', label: 'Amber' },
    { value: 'bg-rose-100 text-rose-700', label: 'Rose' },
    { value: 'bg-cyan-100 text-cyan-700', label: 'Cyan' },
    { value: 'bg-slate-100 text-slate-700', label: 'Slate' },
  ];

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-slate-100 text-slate-600',
    lead: 'bg-blue-100 text-blue-700'
  };

  const activityIcons = {
    note: MessageSquare,
    email: Mail,
    call: PhoneCall,
    meeting: Video,
    task: CheckSquare,
    deal: DollarSign,
    ticket: TicketIcon,
    payment: CreditCard,
    workflow: Workflow
  };

  // AI Sales Assistant functions
  const fetchAIInsights = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await aiAssistantAPI.getInsights(contactId);
      setAiInsights(res.data);
      // Refresh the history to include the new insight
      const historyRes = await aiAssistantAPI.getInsightsHistory(contactId, 10, showArchivedInsights);
      setAiInsightsHistory(historyRes.data || []);
      toast.success('AI insights generated');
    } catch (error) {
      setAiError(error.response?.data?.detail || 'Failed to generate AI insights');
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const handleArchiveInsight = async (insightId, isArchived) => {
    try {
      if (isArchived) {
        await aiAssistantAPI.unarchiveInsight(insightId);
        toast.success('Insight restored');
      } else {
        await aiAssistantAPI.archiveInsight(insightId);
        toast.success('Insight archived');
      }
      // Refresh history
      const historyRes = await aiAssistantAPI.getInsightsHistory(contactId, 10, showArchivedInsights);
      setAiInsightsHistory(historyRes.data || []);
    } catch (error) {
      toast.error('Failed to update insight');
    }
  };

  const createTaskFromRecommendation = async (title, description, priority) => {
    try {
      const response = await aiAssistantAPI.createTaskFromRecommendation(contactId, { title, description, priority });
      console.log('Task created:', response.data);
      toast.success('Task created from recommendation - refreshing tasks...');
      await fetchTabData(); // Refresh tasks (await to ensure completion)
      toast.success('Tasks refreshed');
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const getFilteredTimeline = () => {
    let filtered = timeline;
    
    if (activityFilter !== 'all') {
      filtered = filtered.filter(a => a.activity_type === activityFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.title?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const filteredTimeline = getFilteredTimeline();
  const totalActivities = timeline.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Contact not found</p>
        <Button variant="link" onClick={() => navigate('/contacts')}>Back to Contacts</Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="contact-profile-page">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Contacts
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">Actions <ChevronDown className="h-4 w-4 ml-2" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={openEditContactDialog} data-testid="edit-contact-action">
                <Edit className="h-4 w-4 mr-2" /> Edit Contact
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openPortalAccessDialog()}>
                <User className="h-4 w-4 mr-2" /> Create Portal Access
              </DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" /> Delete Contact
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Customer Status Banner */}
      {contact?.customer_status && (
        <CustomerStatusBanner 
          status={contact.customer_status} 
          statusNote={contact.status_note}
          onEdit={() => setShowStatusDialog(true)}
        />
      )}
      
      {/* Quick Status Buttons (if no status set) */}
      {!contact?.customer_status && (
        <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-dashed border-slate-300">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Set customer status:</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleSetStatus('vip')}>
                <Crown className="h-3 w-3 mr-1" /> VIP
              </Button>
              <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleSetStatus('good')}>
                <ThumbsUp className="h-3 w-3 mr-1" /> Good
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleSetStatus('hostile')}>
                <AlertTriangle className="h-3 w-3 mr-1" /> At Risk
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowStatusDialog(true)}>
                <Flag className="h-3 w-3 mr-1" /> Other
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Three Column Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Contact Info */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          {/* Contact Identity */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-12 w-12 bg-slate-100">
                  <AvatarFallback className="bg-slate-200 text-slate-700 text-lg">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-lg" data-testid="contact-name">
                      {contact.first_name} {contact.last_name}
                    </h2>
                    {/* Customer/Prospect Badge */}
                    <Badge 
                      variant="outline" 
                      className={contact.contact_type === 'customer' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-amber-50 text-amber-700 border-amber-200'}
                      data-testid="contact-type-badge"
                    >
                      {contact.contact_type === 'customer' ? 'Customer' : 'Prospect'}
                    </Badge>
                  </div>
                  {company && (
                    <p className="text-sm text-muted-foreground">{company.name}</p>
                  )}
                  {/* Address */}
                  {(contact.address_city || contact.address_state) && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {[contact.address_city, contact.address_state, contact.address_country].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                {/* Engagement Score Badge */}
                <div className="ml-auto">
                  <EngagementScoreBadge contactId={contactId} size="default" />
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-4">
                <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  {contact.email}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center justify-between border-t pt-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex flex-col items-center gap-1 h-auto py-2" 
                  data-testid="add-note-btn"
                  onClick={() => setNoteDialog(true)}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-xs">Note</span>
                </Button>
                <CreateNoteDialog
                  open={noteDialog}
                  onOpenChange={setNoteDialog}
                  contactId={contactId}
                  onSuccess={() => { fetchTabData(); fetchContactData(); }}
                />

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex flex-col items-center gap-1 h-auto py-2" 
                  data-testid="log-email-btn"
                  onClick={() => setEmailDialog(true)}
                >
                  <Mail className="h-4 w-4" />
                  <span className="text-xs">Email</span>
                </Button>
                <LogEmailDialog
                  open={emailDialog}
                  onOpenChange={setEmailDialog}
                  contactId={contactId}
                  contactEmail={contact?.email}
                  teamMembers={teamUsers}
                  aiAgents={aiAgents}
                  onSuccess={() => { fetchTabData(); fetchContactData(); }}
                />

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex flex-col items-center gap-1 h-auto py-2" 
                  data-testid="log-call-btn"
                  onClick={() => setCallDialog(true)}
                >
                  <PhoneCall className="h-4 w-4" />
                  <span className="text-xs">Call</span>
                </Button>
                <LogCallDialog
                  open={callDialog}
                  onOpenChange={setCallDialog}
                  contactId={contactId}
                  contactPhone={contact?.phone}
                  teamMembers={teamUsers}
                  aiAgents={aiAgents}
                  onSuccess={() => { fetchTabData(); fetchContactData(); }}
                />

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex flex-col items-center gap-1 h-auto py-2" 
                  data-testid="create-task-btn"
                  onClick={() => setTaskDialog(true)}
                >
                  <CheckSquare className="h-4 w-4" />
                  <span className="text-xs">Task</span>
                </Button>
                <CreateTaskDialog
                  open={taskDialog}
                  onOpenChange={setTaskDialog}
                  contactId={contactId}
                  teamMembers={teamUsers}
                  aiAgents={aiAgents}
                  onSuccess={() => { fetchTabData(); fetchContactData(); }}
                />

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex flex-col items-center gap-1 h-auto py-2" 
                  data-testid="schedule-meeting-btn"
                  onClick={() => setMeetingDialog(true)}
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span className="text-xs">Meeting</span>
                </Button>
                <MeetingDialog
                  open={meetingDialog}
                  onOpenChange={setMeetingDialog}
                  contactId={contactId}
                  contact={contact}
                  teamUsers={teamUsers}
                  onSuccess={() => { fetchTabData(); fetchContactData(); }}
                />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 h-auto py-2">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="text-xs">More</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setShowInlineTicketDialog(true)} data-testid="more-create-ticket">
                      <TicketIcon className="h-4 w-4 mr-2" /> Create Ticket
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowInlineDealDialog(true)} data-testid="more-create-deal">
                      <HandshakeIcon className="h-4 w-4 mr-2" /> Create Deal
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowInlinePaymentDialog(true)} data-testid="more-create-payment">
                      <CreditCard className="h-4 w-4 mr-2" /> Record Payment
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowInlineSubscriptionDialog(true)} data-testid="more-create-subscription">
                      <RefreshCw className="h-4 w-4 mr-2" /> Add Subscription
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setAttachmentDialog(true)} data-testid="more-add-attachment">
                      <Paperclip className="h-4 w-4 mr-2" /> Add Attachment
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setContactProductDialog(true)} data-testid="more-assign-product">
                      <Package className="h-4 w-4 mr-2" /> Assign Product
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSegmentsDialog(true)} data-testid="more-manage-segments">
                      <Filter className="h-4 w-4 mr-2" /> Manage Segments
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setPortalAccessDialog(true)} data-testid="invite-portal-btn">
                      <User className="h-4 w-4 mr-2" /> Invite to Portal
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>

          {/* About this contact */}
          <Collapsible open={aboutOpen} onOpenChange={setAboutOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {aboutOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      About this contact
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditContactDialog();
                        }}
                        data-testid="edit-contact-gear-btn"
                      >
                        <Settings className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm">{contact.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Phone Number</p>
                    <p className="text-sm">{contact.phone || '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Company Name</p>
                    <p className="text-sm">{company?.name || '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Job Title</p>
                    <p className="text-sm">{contact.job_title || '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={statusColors[contact.status]}>{contact.status}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="text-sm">{contact.source || '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm">{format(new Date(contact.created_at), 'MMM d, yyyy')}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Segments</p>
                    {contact.segments && contact.segments.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {contact.segments.map(s => {
                          const seg = customerSegments.find(cs => cs.value === s);
                          return (
                            <Badge key={s} className={seg?.color || 'bg-slate-100'} variant="outline">
                              {seg?.label || s}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-6 px-2"
                        onClick={() => setSegmentsDialog(true)}
                      >
                        + Add segments
                      </Button>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Main Content - Activity Feed */}
        <div className="col-span-12 lg:col-span-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {/* Activity Tabs */}
              <Tabs defaultValue="overview" className="w-full">
                <div className="border-b px-2 overflow-x-auto">
                  <TabsList className="h-12 bg-transparent gap-1 flex-nowrap inline-flex min-w-full">
                    <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="overview-tab">
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Activity
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Notes
                    </TabsTrigger>
                    <TabsTrigger value="emails" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Emails
                    </TabsTrigger>
                    <TabsTrigger value="calls" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Calls
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Tasks
                    </TabsTrigger>
                    <TabsTrigger value="ai-tasks" className="data-[state=active]:border-b-2 data-[state=active]:border-purple-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="ai-tasks-tab">
                      <span className="flex items-center gap-1">
                        <Bot className="h-3 w-3 text-purple-600" />
                        AI Tasks
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="meetings" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                      Meetings
                    </TabsTrigger>
                    <TabsTrigger value="tickets" className="data-[state=active]:border-b-2 data-[state=active]:border-red-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="tickets-tab">
                      <span className="flex items-center gap-1">
                        <TicketIcon className="h-3 w-3" />
                        Tickets ({tickets.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="warranties" className="data-[state=active]:border-b-2 data-[state=active]:border-amber-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="warranties-tab">
                      <span className="flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Warranties ({warrantyClaims.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="portal" className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="portal-tab">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Portal ({portalFiles.length + portalChats.length + portalWarranties.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="products" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="products-tab">
                      Products
                    </TabsTrigger>
                    <TabsTrigger value="files" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="files-tab">
                      <span className="flex items-center gap-1">
                        <FolderOpen className="h-3 w-3" />
                        Files
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="campaigns" className="data-[state=active]:border-b-2 data-[state=active]:border-amber-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="campaigns-tab">
                      <span className="flex items-center gap-1">
                        <Megaphone className="h-3 w-3" />
                        Campaigns
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="ai-assistant" className="data-[state=active]:border-b-2 data-[state=active]:border-purple-600 rounded-none bg-transparent text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3" data-testid="ai-assistant-tab">
                      <span className="flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        AI
                      </span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Overview Tab - Task Management */}
                <TabsContent value="overview" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4 space-y-6">
                      {/* Open Tickets Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <TicketIcon className="h-4 w-4 text-red-600" />
                            Open Tickets ({tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length})
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setTicketDialog(true)} data-testid="overview-add-ticket-btn">
                            <Plus className="h-4 w-4 mr-1" /> New Ticket
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 ? (
                            tickets.filter(t => t.status === 'open' || t.status === 'in_progress').slice(0, 5).map(ticket => (
                              <div 
                                key={ticket.id} 
                                className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100 hover:border-red-300 transition-colors cursor-pointer"
                                onClick={() => openTicketDetail(ticket)}
                                data-testid={`overview-ticket-${ticket.id}`}
                              >
                                <AlertCircle className={`h-4 w-4 ${ticket.priority === 'high' || ticket.priority === 'urgent' ? 'text-red-600' : 'text-amber-600'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{ticket.subject}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {ticket.type} • Created {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={`text-xs ${
                                    ticket.status === 'open' ? 'border-blue-300 text-blue-700' : 'border-amber-300 text-amber-700'
                                  }`}>
                                    {ticket.status}
                                  </Badge>
                                  <Badge variant="outline" className={`text-xs ${
                                    ticket.priority === 'high' || ticket.priority === 'urgent' ? 'border-red-300 text-red-700' : 'border-slate-300'
                                  }`}>
                                    {ticket.priority}
                                  </Badge>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                              <TicketIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No open tickets</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Action Items / Tasks Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                            Action Items ({tasks.filter(t => t.status !== 'completed').length})
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setTaskDialog(true)} data-testid="overview-add-task-btn">
                            <Plus className="h-4 w-4 mr-1" /> Add Task
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {tasks.filter(t => t.status !== 'completed').length > 0 ? (
                            tasks.filter(t => t.status !== 'completed').slice(0, 5).map(task => (
                              <div 
                                key={task.id} 
                                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border hover:border-blue-300 transition-colors cursor-pointer"
                                onClick={() => openTaskDetail(task)}
                                data-testid={`overview-task-${task.id}`}
                              >
                                <div className={`w-2 h-2 rounded-full ${
                                  task.priority === 'high' ? 'bg-red-500' :
                                  task.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{task.title}</p>
                                  {task.due_date && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                      <Clock className="h-3 w-3" />
                                      Due {format(new Date(task.due_date), 'MMM d, yyyy h:mm a')}
                                    </p>
                                  )}
                                </div>
                                {task.assigned_to_type === 'ai_agent' && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    <Bot className="h-3 w-3 mr-1" />
                                    AI
                                  </Badge>
                                )}
                                <Badge variant="outline" className={`text-xs ${
                                  task.priority === 'high' ? 'border-red-300 text-red-700' :
                                  task.priority === 'medium' ? 'border-amber-300 text-amber-700' :
                                  'border-slate-300 text-slate-600'
                                }`}>
                                  {task.priority}
                                </Badge>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                              <CheckSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No pending tasks</p>
                              <Button variant="link" size="sm" onClick={() => setTaskDialog(true)}>Create a task</Button>
                            </div>
                          )}
                          
                          {/* Recently Completed AI Tasks with Deliverables */}
                          {tasks.filter(t => t.status === 'completed' && t.assigned_to_type === 'ai_agent').length > 0 && (
                            <div className="mt-4">
                              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                <Bot className="h-3 w-3" />
                                Completed AI Tasks
                              </p>
                              {tasks.filter(t => t.status === 'completed' && t.assigned_to_type === 'ai_agent').slice(0, 3).map(task => (
                                <div 
                                  key={task.id} 
                                  className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100 hover:border-purple-300 transition-colors cursor-pointer mb-2"
                                  onClick={() => openTaskDetail(task)}
                                >
                                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{task.title}</p>
                                    <p className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                                      <FileText className="h-3 w-3" />
                                      {task.attachments?.length > 0 ? 'Document ready' : 'Completed'}
                                    </p>
                                  </div>
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Done</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Active Deals Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <HandshakeIcon className="h-4 w-4 text-emerald-600" />
                            Active Deals ({deals.filter(d => d.stage !== 'closed_lost' && d.stage !== 'closed_won').length})
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setDealDialog(true)} data-testid="overview-add-deal-btn">
                            <Plus className="h-4 w-4 mr-1" /> New Deal
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {deals.filter(d => d.stage !== 'closed_lost' && d.stage !== 'closed_won').length > 0 ? (
                            deals.filter(d => d.stage !== 'closed_lost' && d.stage !== 'closed_won').slice(0, 5).map(deal => (
                              <div 
                                key={deal.id} 
                                className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
                                onClick={() => openDealDetail(deal)}
                                data-testid={`overview-deal-${deal.id}`}
                              >
                                <DollarSign className="h-4 w-4 text-emerald-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{deal.title}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatCurrency(deal.value)} • {deal.expected_close_date ? `Close by ${format(new Date(deal.expected_close_date), 'MMM d')}` : 'No close date'}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">
                                  {deal.stage.replace('_', ' ')}
                                </Badge>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                              <HandshakeIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No active deals</p>
                              <Button variant="link" size="sm" onClick={() => setDealDialog(true)}>Create a deal</Button>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Upcoming Meetings Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-purple-600" />
                            Upcoming Meetings
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setMeetingDialog(true)} data-testid="overview-add-meeting-btn">
                            <Plus className="h-4 w-4 mr-1" /> Schedule
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {meetings.filter(m => new Date(m.start_time) > new Date()).length > 0 ? (
                            meetings.filter(m => new Date(m.start_time) > new Date()).slice(0, 3).map(meeting => (
                              <div 
                                key={meeting.id} 
                                className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100 hover:border-purple-300 transition-colors cursor-pointer"
                                onClick={() => openMeetingDetail(meeting)}
                                data-testid={`overview-meeting-${meeting.id}`}
                              >
                                <Video className="h-4 w-4 text-purple-600 mt-0.5" />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{meeting.title}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {format(new Date(meeting.start_time), 'EEE, MMM d • h:mm a')}
                                  </p>
                                  {meeting.meeting_link && (
                                    <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer" 
                                       className="text-xs text-purple-600 hover:underline flex items-center gap-1 mt-1"
                                       onClick={(e) => e.stopPropagation()}>
                                      <ExternalLink className="h-3 w-3" /> Join meeting
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                              <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No upcoming meetings</p>
                              <Button variant="link" size="sm" onClick={() => setMeetingDialog(true)}>Schedule a meeting</Button>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Active Subscriptions Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 text-indigo-600" />
                            Active Subscriptions ({subscriptions.filter(s => s.status === 'active').length})
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setSubscriptionDialog(true)} data-testid="overview-add-subscription-btn">
                            <Plus className="h-4 w-4 mr-1" /> Add
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {subscriptions.filter(s => s.status === 'active').length > 0 ? (
                            subscriptions.filter(s => s.status === 'active').map(sub => (
                              <div 
                                key={sub.id} 
                                className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 hover:border-indigo-300 transition-colors cursor-pointer"
                                onClick={() => openSubscriptionDetail(sub)}
                                data-testid={`overview-subscription-${sub.id}`}
                              >
                                <RefreshCw className="h-4 w-4 text-indigo-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{sub.plan_name}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatCurrency(sub.plan_price)}/{sub.billing_cycle}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-700">
                                  {sub.status}
                                </Badge>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                              <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No active subscriptions</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Products Summary Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Package className="h-4 w-4 text-blue-600" />
                            Products Summary ({contactProducts.length})
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setContactProductDialog(true)} data-testid="overview-add-product-btn">
                            <Plus className="h-4 w-4 mr-1" /> Assign
                          </Button>
                        </div>
                        {contactProducts.length > 0 ? (
                          <div className="space-y-3">
                            {/* Stats Cards */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="p-3 bg-blue-50 rounded-lg text-center">
                                <p className="text-2xl font-bold text-blue-600">{contactProducts.length}</p>
                                <p className="text-xs text-muted-foreground">Products</p>
                              </div>
                              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                                <p className="text-lg font-bold text-emerald-600">
                                  {formatCurrency(contactProducts.reduce((sum, cp) => sum + (cp.purchase_price || cp.product?.price || 0), 0))}
                                </p>
                                <p className="text-xs text-muted-foreground">Total Value</p>
                              </div>
                              <div className="p-3 bg-amber-50 rounded-lg text-center">
                                <p className="text-2xl font-bold text-amber-600">
                                  {contactProducts.filter(cp => cp.warranty_expiry && new Date(cp.warranty_expiry) > new Date() && new Date(cp.warranty_expiry) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)).length}
                                </p>
                                <p className="text-xs text-muted-foreground">Expiring Soon</p>
                              </div>
                            </div>
                            
                            {/* Warranty Alerts */}
                            {contactProducts.filter(cp => {
                              if (!cp.warranty_expiry) return false;
                              const expiry = new Date(cp.warranty_expiry);
                              const now = new Date();
                              const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                              return expiry > now && expiry < thirtyDays;
                            }).length > 0 && (
                              <div className="p-3 bg-amber-100 border border-amber-300 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertCircle className="h-4 w-4 text-amber-600" />
                                  <span className="font-medium text-sm text-amber-800">Warranty Expiring Soon</span>
                                </div>
                                <div className="space-y-1">
                                  {contactProducts.filter(cp => {
                                    if (!cp.warranty_expiry) return false;
                                    const expiry = new Date(cp.warranty_expiry);
                                    const now = new Date();
                                    const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                                    return expiry > now && expiry < thirtyDays;
                                  }).map(cp => (
                                    <div 
                                      key={cp.id} 
                                      className="text-xs text-amber-800 flex justify-between cursor-pointer hover:bg-amber-200 p-1 rounded"
                                      onClick={() => openContactProductDetail(cp)}
                                    >
                                      <span>{cp.product?.name}</span>
                                      <span>Expires {format(new Date(cp.warranty_expiry), 'MMM d, yyyy')}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Expired Warranties */}
                            {contactProducts.filter(cp => cp.warranty_expiry && new Date(cp.warranty_expiry) < new Date()).length > 0 && (
                              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertCircle className="h-4 w-4 text-red-600" />
                                  <span className="font-medium text-sm text-red-700">Expired Warranties ({contactProducts.filter(cp => cp.warranty_expiry && new Date(cp.warranty_expiry) < new Date()).length})</span>
                                </div>
                                <div className="space-y-1">
                                  {contactProducts.filter(cp => cp.warranty_expiry && new Date(cp.warranty_expiry) < new Date()).slice(0, 3).map(cp => (
                                    <div 
                                      key={cp.id} 
                                      className="text-xs text-red-700 flex justify-between cursor-pointer hover:bg-red-100 p-1 rounded"
                                      onClick={() => openContactProductDetail(cp)}
                                    >
                                      <span>{cp.product?.name}</span>
                                      <span>Expired {format(new Date(cp.warranty_expiry), 'MMM d, yyyy')}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Recent Products */}
                            <div className="space-y-2">
                              {contactProducts.slice(0, 3).map(cp => (
                                <div 
                                  key={cp.id}
                                  className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                                  onClick={() => openContactProductDetail(cp)}
                                  data-testid={`overview-product-${cp.id}`}
                                >
                                  <Package className="h-4 w-4 text-blue-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{cp.product?.name}</p>
                                    {cp.serial_numbers && cp.serial_numbers.length > 0 && (
                                      <p className="text-xs text-muted-foreground">S/N: {cp.serial_numbers.join(', ')}</p>
                                    )}
                                  </div>
                                  <span className="text-sm font-medium text-emerald-600">
                                    {formatCurrency(cp.purchase_price || cp.product?.price || 0)}
                                  </span>
                                </div>
                              ))}
                              {contactProducts.length > 3 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="w-full text-blue-600"
                                  onClick={() => document.querySelector('[data-testid="products-tab"]')?.click()}
                                >
                                  View all {contactProducts.length} products
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No products assigned</p>
                            <Button variant="link" size="sm" onClick={() => setContactProductDialog(true)}>
                              Assign a product
                            </Button>
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* Pinned Notes Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Pin className="h-4 w-4 text-amber-600" />
                            Pinned Notes
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setNoteDialog(true)} data-testid="overview-add-note-btn">
                            <Plus className="h-4 w-4 mr-1" /> Add Note
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {notes.filter(n => n.is_pinned).length > 0 ? (
                            notes.filter(n => n.is_pinned).map(note => (
                              <div 
                                key={note.id} 
                                className="p-3 bg-amber-50 rounded-lg border border-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                                onClick={() => openNoteDetail(note)}
                                data-testid={`overview-note-${note.id}`}
                              >
                                <p className="text-sm line-clamp-3">{note.content}</p>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                                  {note.author && ` • by ${note.author.full_name || note.author.email}`}
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-2">No pinned notes</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="m-0">
                  {/* Filters */}
                  <div className="px-4 py-3 border-b bg-slate-50/50">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search activities..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9 bg-white"
                          data-testid="activity-search"
                        />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-9">
                            <Filter className="h-4 w-4 mr-2" />
                            Filter activity ({filteredTimeline.length}/{totalActivities})
                            <ChevronDown className="h-4 w-4 ml-2" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setActivityFilter('all')}>All Activities</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('note')}>Notes</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('email')}>Emails</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('call')}>Calls</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('meeting')}>Meetings</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('task')}>Tasks</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('deal')}>Deals</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActivityFilter('ticket')}>Tickets</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => fetchContactData()}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Timeline */}
                  <ScrollArea className="h-[500px]">
                    <div className="p-4">
                      {filteredTimeline.length > 0 ? (
                        <div className="space-y-4">
                          {filteredTimeline.map((activity, index) => {
                            const Icon = activityIcons[activity.activity_type] || MessageSquare;
                            return (
                              <div key={activity.id || index} className="flex gap-3 group">
                                <div className="flex flex-col items-center">
                                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                    <Icon className="h-4 w-4 text-slate-600" />
                                  </div>
                                  {index < filteredTimeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-2" />}
                                </div>
                                <div 
                                  className="flex-1 bg-white border rounded-lg p-3 hover:shadow-sm hover:border-blue-200 transition-all cursor-pointer"
                                  onClick={() => openActivityDetail(activity)}
                                  data-testid={`activity-item-${activity.id || index}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="font-medium text-sm">{activity.title}</p>
                                        <Eye className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </div>
                                      {activity.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-2">{activity.description}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-2">
                                        <Avatar className="h-5 w-5">
                                          <AvatarFallback className="text-xs bg-slate-100">
                                            {activity.author?.full_name?.[0] || activity.author?.email?.[0] || 'S'}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs text-muted-foreground">
                                          {activity.author?.full_name || activity.author?.email || 'System'}
                                        </span>
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                      {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-muted-foreground">No activity yet</p>
                          <p className="text-sm text-muted-foreground mt-1">Add a note, log a call, or send an email to get started</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="m-0">
                  <NotesTab 
                    notes={notes} 
                    onNoteClick={openNoteDetail}
                    onDeleteNote={handleDeleteNote}
                  />
                </TabsContent>

                {/* Emails Tab */}
                <TabsContent value="emails" className="m-0">
                  <EmailsTab 
                    emails={emails} 
                    onEmailClick={openEmailDetail}
                  />
                </TabsContent>

                {/* Calls Tab */}
                <TabsContent value="calls" className="m-0">
                  <CallsTab 
                    calls={calls} 
                    onCallClick={openCallDetail}
                  />
                </TabsContent>

                {/* Tasks Tab */}
                <TabsContent value="tasks" className="m-0">
                  <TasksTab 
                    tasks={tasks} 
                    onTaskClick={openTaskDetail}
                  />
                </TabsContent>

                {/* AI Tasks Tab - Tasks assigned to AI agents */}
                <TabsContent value="ai-tasks" className="m-0">
                  <AITasksTab 
                    contactId={contactId}
                    tasks={tasks}
                  />
                </TabsContent>

                {/* Meetings Tab */}
                <TabsContent value="meetings" className="m-0">
                  <MeetingsTab 
                    meetings={meetings} 
                    onMeetingClick={openMeetingDetail}
                  />
                </TabsContent>

                {/* Tickets Tab */}
                <TabsContent value="tickets" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <TicketIcon className="h-4 w-4 text-red-600" />
                          Support Tickets ({tickets.length})
                        </h3>
                        <Button variant="outline" size="sm" onClick={() => setTicketDialog(true)}>
                          <Plus className="h-4 w-4 mr-1" /> Create Ticket
                        </Button>
                      </div>
                      {tickets.length > 0 ? (
                        <div className="space-y-3">
                          {tickets.map((ticket) => (
                            <div 
                              key={ticket.id}
                              className="p-4 bg-slate-50 rounded-lg border hover:border-red-300 transition-colors cursor-pointer"
                              onClick={() => openTicketDetail(ticket)}
                              data-testid={`contact-ticket-${ticket.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                    ticket.priority === 'urgent' ? 'bg-red-100' :
                                    ticket.priority === 'high' ? 'bg-amber-100' :
                                    'bg-slate-100'
                                  }`}>
                                    <TicketIcon className={`h-5 w-5 ${
                                      ticket.priority === 'urgent' ? 'text-red-600' :
                                      ticket.priority === 'high' ? 'text-amber-600' :
                                      'text-slate-600'
                                    }`} />
                                  </div>
                                  <div>
                                    <p className="font-medium">{ticket.subject}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      #{ticket.ticket_number || ticket.id?.slice(0, 8)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right space-y-1">
                                  <Badge className={
                                    ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                                    ticket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                    ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-slate-100 text-slate-700'
                                  }>
                                    {ticket.status?.replace('_', ' ')}
                                  </Badge>
                                  <p className="text-xs text-muted-foreground">
                                    {format(new Date(ticket.created_at), 'MMM d, yyyy')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <TicketIcon className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          <p>No support tickets</p>
                          <p className="text-sm">Create a ticket to track customer issues</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Warranty Claims Tab */}
                <TabsContent value="warranties" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <Shield className="h-4 w-4 text-amber-600" />
                          Warranty Claims ({warrantyClaims.length})
                        </h3>
                      </div>
                      {warrantyClaims.length > 0 ? (
                        <div className="space-y-3">
                          {warrantyClaims.map((claim) => (
                            <div 
                              key={claim.id}
                              className="p-4 bg-slate-50 rounded-lg border hover:border-amber-300 transition-colors"
                              data-testid={`warranty-claim-${claim.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                    claim.status === 'pending' ? 'bg-amber-100' :
                                    claim.status === 'approved' ? 'bg-emerald-100' :
                                    claim.status === 'rejected' ? 'bg-red-100' :
                                    'bg-slate-100'
                                  }`}>
                                    <Shield className={`h-5 w-5 ${
                                      claim.status === 'pending' ? 'text-amber-600' :
                                      claim.status === 'approved' ? 'text-emerald-600' :
                                      claim.status === 'rejected' ? 'text-red-600' :
                                      'text-slate-600'
                                    }`} />
                                  </div>
                                  <div>
                                    <p className="font-medium">Claim #{claim.claim_number}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-2">{claim.issue_description}</p>
                                    {claim.product_name && (
                                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                        <Package className="h-3 w-3" />
                                        {claim.product_name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right space-y-1">
                                  <Badge className={
                                    claim.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    claim.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                    claim.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    claim.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-700'
                                  }>
                                    {claim.status?.replace('_', ' ')}
                                  </Badge>
                                  <p className="text-xs text-muted-foreground">
                                    {format(new Date(claim.created_at), 'MMM d, yyyy')}
                                  </p>
                                  <p className="text-xs text-muted-foreground capitalize">
                                    {claim.preferred_resolution}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Shield className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          <p>No warranty claims</p>
                          <p className="text-sm">Warranty claims from the customer portal will appear here</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Portal Tab - Customer portal activity */}
                <TabsContent value="portal" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4 space-y-6" data-testid="portal-tab-content">
                      {/* Warranty Registrations */}
                      <div>
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-amber-600" /> Warranty Registrations ({portalWarranties.length})
                        </h3>
                        {portalWarranties.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No product registrations.</p>
                        ) : (
                          <div className="space-y-2">
                            {portalWarranties.map(w => (
                              <div key={w.id} className="border rounded-lg p-3 text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{w.product_name || 'Product'}</span>
                                  <Badge variant="outline">{w.status === 'pending' ? 'Pending Approval' : w.status}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Unit: {w.serial_number || 'N/A'} · Pump: {w.pump_serial_number || 'N/A'}
                                  {w.extended_warranty_status && w.extended_warranty_status !== 'none' ? ` · Extended: ${w.extended_warranty_status}` : ''}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* File Uploads */}
                      <div>
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
                          <Paperclip className="h-4 w-4 text-teal-600" /> File Uploads ({portalFiles.length})
                        </h3>
                        {portalFiles.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No files uploaded from the portal.</p>
                        ) : (
                          <div className="space-y-2">
                            {portalFiles.map(f => (
                              <div key={f.id} className="border rounded-lg p-3 text-sm flex items-center justify-between">
                                <div>
                                  <span className="font-medium">{f.title || f.file_name}</span>
                                  <p className="text-xs text-muted-foreground">{f.category} · {f.file_type}</p>
                                </div>
                                <Badge variant="outline">{f.status}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Live Chats */}
                      <div>
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
                          <MessageSquare className="h-4 w-4 text-purple-600" /> Live Chats ({portalChats.length})
                        </h3>
                        {portalChats.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No chat sessions.</p>
                        ) : (
                          <div className="space-y-2">
                            {portalChats.map(c => (
                              <div key={c.id} className="border rounded-lg p-3 text-sm flex items-center justify-between">
                                <div>
                                  <span className="font-medium">{c.visitor_name || 'Chat session'}</span>
                                  <p className="text-xs text-muted-foreground">{c.message_count || 0} messages</p>
                                </div>
                                <Badge variant="outline">{c.status}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Products Tab - Customer Products */}
                <TabsContent value="products" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          Products Owned ({contactProducts.length})
                        </h3>
                        <Button variant="outline" size="sm" onClick={() => setContactProductDialog(true)} data-testid="add-contact-product-btn">
                          <Plus className="h-4 w-4 mr-1" /> Assign Product
                        </Button>
                      </div>
                      {contactProducts.length > 0 ? (
                        <div className="space-y-3">
                          {contactProducts.map((cp) => (
                            <div 
                              key={cp.id}
                              className="p-4 bg-slate-50 rounded-lg border hover:border-blue-300 transition-colors cursor-pointer"
                              onClick={() => openContactProductDetail(cp)}
                              data-testid={`contact-product-${cp.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Package className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{cp.product?.name || 'Unknown Product'}</p>
                                    <p className="text-sm text-muted-foreground">{cp.product?.sku}</p>
                                    {cp.serial_numbers && cp.serial_numbers.length > 0 && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        S/N: {cp.serial_numbers.map((sn, i) => (
                                          <code key={i} className="bg-slate-200 px-1 rounded mr-1">{sn}</code>
                                        ))}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {cp.purchase_price != null && (
                                    <p className="font-semibold text-emerald-600">{formatCurrency(cp.purchase_price)}</p>
                                  )}
                                  {(cp.discount_percent > 0 || cp.discount_amount > 0) && (
                                    <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 mt-1">
                                      {cp.discount_percent > 0 ? `${cp.discount_percent}% off` : `$${cp.discount_amount} off`}
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className={`text-xs mt-1 ml-1 ${
                                    cp.status === 'active' ? 'border-emerald-300 text-emerald-700' :
                                    cp.status === 'returned' ? 'border-red-300 text-red-700' :
                                    'border-slate-300 text-slate-600'
                                  }`}>
                                    {cp.status}
                                  </Badge>
                                </div>
                              </div>
                              {cp.purchase_date && (
                                <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs text-muted-foreground">
                                  <span>Purchased: {format(new Date(cp.purchase_date), 'MMM d, yyyy')}</span>
                                  {cp.warranty_expiry && (
                                    <span className={new Date(cp.warranty_expiry) > new Date() ? 'text-emerald-600' : 'text-red-600'}>
                                      Warranty: {new Date(cp.warranty_expiry) > new Date() ? 'Valid' : 'Expired'} until {format(new Date(cp.warranty_expiry), 'MMM d, yyyy')}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-muted-foreground">No products assigned</p>
                          <Button variant="link" size="sm" onClick={() => setContactProductDialog(true)}>
                            Assign a product
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Files/Gallery Tab */}
                <TabsContent value="files" className="m-0">
                  <FilesTab 
                    attachments={attachments} 
                    onUploadClick={() => setAttachmentDialog(true)}
                  />
                </TabsContent>

                {/* Campaigns Tab - Marketing & Sales Campaign History */}
                <TabsContent value="campaigns" className="m-0">
                  <CampaignsTab 
                    campaigns={contactCampaigns}
                    stats={campaignStats}
                    contactId={contactId}
                    onRefresh={fetchTabData}
                  />
                </TabsContent>

                {/* AI Sales Assistant Tab */}
                <TabsContent value="ai-assistant" className="m-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4 space-y-4">
                      {/* Header with Generate Button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-purple-600" />
                            AI Sales Assistant
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            AI-powered insights and recommendations
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setShowArchivedInsights(!showArchivedInsights)}
                            data-testid="toggle-archived-insights-btn"
                          >
                            <History className="h-4 w-4 mr-1" />
                            {showArchivedInsights ? 'Hide Archived' : 'Show History'}
                          </Button>
                          <Button 
                            onClick={fetchAIInsights} 
                            disabled={aiLoading}
                            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                            data-testid="generate-ai-insights-btn"
                          >
                            {aiLoading ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                New Analysis
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {aiError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                          <AlertCircle className="h-4 w-4 inline mr-2" />
                          {aiError}
                        </div>
                      )}

                      {/* AI Insights History */}
                      {aiInsightsHistory.length > 0 && (
                        <Card className="bg-slate-50">
                          <CardContent className="pt-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
                              <History className="h-4 w-4 text-slate-500" />
                              Analysis History ({aiInsightsHistory.length})
                            </h4>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {aiInsightsHistory.map((history, idx) => (
                                <div 
                                  key={history.id} 
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                                    aiInsights?.insight_id === history.id ? 'bg-purple-100 border border-purple-300' : 'bg-white hover:bg-slate-100'
                                  } ${history.is_archived ? 'opacity-60' : ''}`}
                                  onClick={() => {
                                    setAiInsights({
                                      ...history.insights,
                                      generated_at: history.generated_at,
                                      insight_id: history.id
                                    });
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <Badge className={`${
                                      (history.insights?.overall_health_score || 0) >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                      (history.insights?.overall_health_score || 0) >= 50 ? 'bg-amber-100 text-amber-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {history.insights?.overall_health_score || 0}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(history.generated_at), 'MMM d, yyyy h:mm a')}
                                    </span>
                                    {history.is_archived && (
                                      <Badge variant="outline" className="text-xs">Archived</Badge>
                                    )}
                                    {idx === 0 && !history.is_archived && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">Latest</Badge>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleArchiveInsight(history.id, history.is_archived);
                                    }}
                                  >
                                    <Archive className={`h-3 w-3 ${history.is_archived ? 'text-emerald-600' : 'text-slate-400'}`} />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {aiInsights ? (
                        <div className="space-y-4">
                          {/* Health Score & Engagement */}
                          <div className="grid grid-cols-2 gap-4">
                            <Card className="bg-gradient-to-br from-purple-50 to-blue-50">
                              <CardContent className="pt-4">
                                <div className="text-center">
                                  <p className="text-4xl font-bold text-purple-600">{aiInsights.overall_health_score || 0}</p>
                                  <p className="text-sm text-muted-foreground">Health Score</p>
                                </div>
                              </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-emerald-50 to-cyan-50">
                              <CardContent className="pt-4">
                                <div className="text-center">
                                  <Badge className={
                                    aiInsights.engagement_level === 'high' ? 'bg-emerald-100 text-emerald-700' :
                                    aiInsights.engagement_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }>
                                    {aiInsights.engagement_level?.toUpperCase() || 'N/A'}
                                  </Badge>
                                  <p className="text-sm text-muted-foreground mt-2">Engagement Level</p>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          {/* Summary */}
                          {aiInsights.summary && (
                            <Card>
                              <CardContent className="pt-4">
                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                  <Lightbulb className="h-4 w-4 text-amber-500" />
                                  Summary
                                </h4>
                                <p className="text-sm text-muted-foreground">{aiInsights.summary}</p>
                              </CardContent>
                            </Card>
                          )}

                          {/* Pipeline Analysis */}
                          {aiInsights.pipeline_analyses && aiInsights.pipeline_analyses.length > 0 && (
                            <Card>
                              <CardContent className="pt-4">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <TrendingUp className="h-4 w-4 text-blue-500" />
                                  Pipeline Analysis
                                </h4>
                                <div className="space-y-3">
                                  {aiInsights.pipeline_analyses.map((analysis, idx) => (
                                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium">{analysis.deal_title}</span>
                                        <Badge className={
                                          analysis.closure_probability >= 70 ? 'bg-emerald-100 text-emerald-700' :
                                          analysis.closure_probability >= 40 ? 'bg-amber-100 text-amber-700' :
                                          'bg-red-100 text-red-700'
                                        }>
                                          {analysis.closure_probability}% Closure
                                        </Badge>
                                      </div>
                                      {analysis.risk_factors && analysis.risk_factors.length > 0 && (
                                        <div className="mb-2">
                                          <span className="text-xs text-red-600 font-medium">Risk Factors:</span>
                                          <ul className="text-xs text-muted-foreground ml-4 list-disc">
                                            {analysis.risk_factors.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                                          </ul>
                                        </div>
                                      )}
                                      {analysis.next_best_action && (
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                          <span className="text-xs text-emerald-600">
                                            <Target className="h-3 w-3 inline mr-1" />
                                            Next: {analysis.next_best_action}
                                          </span>
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            className="h-6 text-xs"
                                            onClick={() => createTaskFromRecommendation(
                                              analysis.next_best_action,
                                              `Deal: ${analysis.deal_title}\n\nRecommended action from AI analysis.`,
                                              'high'
                                            )}
                                          >
                                            <Plus className="h-3 w-3 mr-1" /> Create Task
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Recommendations */}
                          {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                            <Card>
                              <CardContent className="pt-4">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <ListChecks className="h-4 w-4 text-purple-500" />
                                  Recommendations
                                </h4>
                                <div className="space-y-3">
                                  {aiInsights.recommendations.map((rec, idx) => (
                                    <div key={idx} className="p-3 border rounded-lg">
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <Badge variant="outline" className={
                                            rec.type === 'risk' ? 'border-red-300 text-red-700' :
                                            rec.type === 'upsell' ? 'border-emerald-300 text-emerald-700' :
                                            rec.type === 'pipeline' ? 'border-blue-300 text-blue-700' :
                                            'border-purple-300 text-purple-700'
                                          }>
                                            {rec.type?.toUpperCase()}
                                          </Badge>
                                          <Badge variant="outline" className={`ml-2 ${
                                            rec.priority === 'urgent' ? 'border-red-300 text-red-700' :
                                            rec.priority === 'high' ? 'border-amber-300 text-amber-700' :
                                            'border-slate-300 text-slate-600'
                                          }`}>
                                            {rec.priority}
                                          </Badge>
                                        </div>
                                      </div>
                                      <h5 className="font-medium text-sm">{rec.title}</h5>
                                      <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                                      {rec.action_items && rec.action_items.length > 0 && (
                                        <div className="mt-2 pt-2 border-t">
                                          <span className="text-xs font-medium">Action Items:</span>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {rec.action_items.map((action, ai) => (
                                              <Button
                                                key={ai}
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 text-xs bg-slate-100 hover:bg-slate-200"
                                                onClick={() => createTaskFromRecommendation(action, rec.description, rec.priority)}
                                              >
                                                <Plus className="h-3 w-3 mr-1" /> {action.length > 30 ? action.slice(0, 30) + '...' : action}
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Playbook Suggestions */}
                          {aiInsights.playbook_suggestions && aiInsights.playbook_suggestions.length > 0 && (
                            <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
                              <CardContent className="pt-4">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <PlayCircle className="h-4 w-4 text-purple-500" />
                                  Suggested Playbooks
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {aiInsights.playbook_suggestions.map((playbook, idx) => (
                                    <Badge key={idx} variant="outline" className="bg-white">
                                      {playbook}
                                    </Badge>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Generated timestamp */}
                          <p className="text-xs text-muted-foreground text-center">
                            Generated: {aiInsights.generated_at ? format(new Date(aiInsights.generated_at), 'PPp') : 'N/A'}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Sparkles className="h-16 w-16 text-purple-200 mx-auto mb-4" />
                          <h3 className="font-semibold text-lg mb-2">AI Sales Assistant</h3>
                          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                            Generate AI-powered insights including sales recommendations, pipeline analysis, 
                            closure probability, and actionable tasks to improve outcomes.
                          </p>
                          <Button 
                            onClick={fetchAIInsights}
                            disabled={aiLoading}
                            className="bg-gradient-to-r from-purple-600 to-blue-600"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Insights
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Associated Records */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          {/* Companies */}
          <Collapsible open={companiesOpen} onOpenChange={setCompaniesOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {companiesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Companies ({company ? 1 : 0})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setCompanyDialog(true); }}
                      data-testid="add-company-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {company ? (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Building className="h-4 w-4 text-slate-600" />
                        <Link to={`/companies/${company.id}`} className="font-medium text-sm hover:text-blue-600">
                          {company.name}
                        </Link>
                        <Badge variant="outline" className="text-xs">Primary</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{company.industry || 'No industry'}</p>
                      {company.website && (
                        <a href={company.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                          {company.website} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No company associated</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Deals */}
          <Collapsible open={dealsOpen} onOpenChange={setDealsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {dealsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Deals ({deals.length})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setDealDialog(true); }}
                      data-testid="add-deal-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  {deals.length > 0 ? deals.slice(0, 5).map(deal => (
                    <div 
                      key={deal.id} 
                      className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => openDealDetail(deal)}
                      data-testid={`sidebar-deal-${deal.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <HandshakeIcon className="h-4 w-4 text-slate-600" />
                        <span className="font-medium text-sm">{deal.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Amount: {formatCurrency(deal.value)}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">Stage:</span>
                        <Badge variant="outline" className="text-xs capitalize">{deal.stage.replace('_', ' ')}</Badge>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No deals</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>


          {/* Tickets */}
          <Collapsible open={ticketsOpen} onOpenChange={setTicketsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {ticketsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Tickets ({tickets.length})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setTicketDialog(true); }}
                      data-testid="add-ticket-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  {tickets.length > 0 ? tickets.slice(0, 5).map(ticket => (
                    <div 
                      key={ticket.id} 
                      className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => openTicketDetail(ticket)}
                      data-testid={`sidebar-ticket-${ticket.id}`}
                    >
                      <p className="font-medium text-sm">{ticket.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{ticket.type}</Badge>
                        <Badge className={
                          ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                          ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }>{ticket.status}</Badge>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No tickets</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Payments */}
          <Collapsible open={paymentsOpen} onOpenChange={setPaymentsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {paymentsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Payments ({payments.length})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setPaymentDialog(true); }}
                      data-testid="add-payment-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  <div className="text-center py-1 mb-2">
                    <span className="text-lg font-semibold text-emerald-600">{formatCurrency(profileSummary?.total_payments)}</span>
                    <span className="text-xs text-muted-foreground ml-1">total</span>
                  </div>
                  {payments.length > 0 ? payments.slice(0, 5).map(payment => (
                    <div 
                      key={payment.id} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => openPaymentDetail(payment)}
                      data-testid={`sidebar-payment-${payment.id}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-muted-foreground">{payment.method}</p>
                      </div>
                      <Badge className={`text-xs ${payment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {payment.status}
                      </Badge>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No payments</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Subscriptions */}
          <Collapsible open={subscriptionsOpen} onOpenChange={setSubscriptionsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {subscriptionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Subscriptions ({subscriptions.length})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setSubscriptionDialog(true); }}
                      data-testid="add-subscription-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  {subscriptions.length > 0 ? subscriptions.map(sub => (
                    <div 
                      key={sub.id} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => openSubscriptionDetail(sub)}
                      data-testid={`sidebar-subscription-${sub.id}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{sub.plan_name}</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(sub.plan_price)}/{sub.billing_cycle}</p>
                      </div>
                      <Badge className={sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {sub.status}
                      </Badge>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No subscriptions</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Attachments */}
          <Collapsible open={attachmentsOpen} onOpenChange={setAttachmentsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {attachmentsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Attachments ({attachments.length})
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-blue-600 text-xs"
                      onClick={(e) => { e.stopPropagation(); setAttachmentDialog(true); }}
                      data-testid="add-attachment-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  {attachments.length > 0 ? attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <Paperclip className="h-4 w-4 text-slate-500" />
                      <a href={att.file_url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate">
                        {att.filename}
                      </a>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No attachments</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Playbook Notes */}
          <PlaybookNotesSection 
            entityType="contact" 
            entityId={contactId} 
            className="border-0 shadow-sm"
          />
        </div>
      </div>

      {/* Ticket Dialog - Extracted */}
      <SimpleTicketDialog
        open={ticketDialog}
        onOpenChange={setTicketDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Subscription Dialog - Extracted */}
      <SimpleSubscriptionDialog
        open={subscriptionDialog}
        onOpenChange={setSubscriptionDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Attachment Dialog - Extracted */}
      <AttachmentDialog
        open={attachmentDialog}
        onOpenChange={setAttachmentDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Payment Dialog - Extracted */}
      <SimplePaymentDialog
        open={paymentDialog}
        onOpenChange={setPaymentDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Company Dialog - Extracted */}
      <CompanyDialog
        open={companyDialog}
        onOpenChange={setCompanyDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Deal Dialog - Extracted */}
      <SimpleDealDialog
        open={dealDialog}
        onOpenChange={setDealDialog}
        contactId={contactId}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      {/* Activity Detail Dialog - Extracted */}
      <ActivityDetailDialog
        open={activityDetailDialog}
        onOpenChange={setActivityDetailDialog}
        activity={selectedActivity}
        activityIcons={activityIcons}
      />

      {/* Note Detail Dialog - Extracted */}
      <NoteDetailDialog
        open={noteDetailDialog}
        onOpenChange={(open) => { setNoteDetailDialog(open); if (!open) setEditMode(false); }}
        note={selectedNote}
        onUpdate={handleUpdateNote}
      />

      {/* Task Detail Dialog */}
      <Dialog open={taskDetailDialog} onOpenChange={(open) => {
        setTaskDetailDialog(open);
        if (!open) {
          setSelectedTaskAIHistory(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5" />
              {editMode ? 'Edit Task' : 'Task Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            editMode ? (
              <form onSubmit={handleUpdateTask} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={taskForm.title}
                    onChange={(e) => setTaskForm(p => ({ ...p, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={taskForm.priority} onValueChange={(v) => setTaskForm(p => ({ ...p, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={taskForm.status} onValueChange={(v) => setTaskForm(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="datetime-local"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm(p => ({ ...p, due_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select value={taskForm.assigned_to || ''} onValueChange={(v) => setTaskForm(p => ({ ...p, assigned_to: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                    <SelectContent>
                      {teamUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{selectedTask.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={
                      selectedTask.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      selectedTask.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }>{selectedTask.status}</Badge>
                    <Badge className={
                      selectedTask.priority === 'high' ? 'bg-red-100 text-red-700' :
                      selectedTask.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }>{selectedTask.priority}</Badge>
                    {selectedTask.assigned_to_type === 'ai_agent' && (
                      <Badge className="bg-purple-100 text-purple-700">
                        <Bot className="h-3 w-3 mr-1" />
                        AI
                      </Badge>
                    )}
                  </div>
                </div>
                {selectedTask.description && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm">{selectedTask.description}</p>
                  </div>
                )}
                
                {/* AI Processing Status */}
                {selectedTask.assigned_to_type === 'ai_agent' && (
                  <div className="border-t pt-4">
                    <AIProcessingIndicator 
                      taskId={selectedTask.id}
                    />
                  </div>
                )}
                
                {/* AI Deliverables */}
                {selectedTaskAIHistory?.deliverables?.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      AI Generated Documents
                    </h4>
                    <div className="space-y-2">
                      {selectedTaskAIHistory.deliverables.map((deliverable, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded">
                              <FileText className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{deliverable.title || deliverable.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {deliverable.filename?.endsWith('.xlsx') ? 'Excel Spreadsheet' : 'Word Document'}
                                {deliverable.created_at && ` • ${format(new Date(deliverable.created_at), 'MMM d, h:mm a')}`}
                              </p>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={async () => {
                              try {
                                toast.info('Downloading...');
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${deliverable.file_url}`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                if (!response.ok) {
                                  console.error('Download response:', response.status, response.statusText);
                                  throw new Error('Download failed');
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = deliverable.filename || 'document';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                                toast.success('Download complete!');
                              } catch (error) {
                                console.error('Download error:', error);
                                toast.error('Failed to download file');
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* AI Analysis/Reasoning */}
                {selectedTaskAIHistory?.deliverables?.some(d => d.content) && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      AI Analysis & Reasoning
                    </h4>
                    <ScrollArea className="h-48 rounded-lg border">
                      <div className="p-3 text-sm whitespace-pre-wrap bg-slate-50">
                        {selectedTaskAIHistory.deliverables.find(d => d.content)?.content || 'No analysis available'}
                      </div>
                    </ScrollArea>
                  </div>
                )}
                
                {/* Task Attachments */}
                {selectedTask.attachments?.length > 0 && !selectedTaskAIHistory?.deliverables?.length && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Attachments
                    </h4>
                    <div className="space-y-2">
                      {selectedTask.attachments.map((attachment, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              toast.info('Downloading...');
                              const token = localStorage.getItem('token');
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${attachment}`, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              if (!response.ok) throw new Error('Download failed');
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = attachment.split('/').pop() || 'document';
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                              toast.success('Download complete!');
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Failed to download file');
                            }
                          }}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download Document
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-2 text-sm border-t pt-4">
                  {selectedTask.due_date && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Due: {format(new Date(selectedTask.due_date), 'PPp')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Created: {format(new Date(selectedTask.created_at), 'PPp')}</span>
                  </div>
                  {selectedTask.assigned_to_name && (
                    <div className="flex items-center gap-2">
                      {selectedTask.assigned_to_type === 'ai_agent' ? (
                        <Bot className="h-4 w-4 text-purple-600" />
                      ) : (
                        <User className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span>Assigned to: {selectedTask.assigned_to_name}</span>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditMode(true)}>
                    <Edit className="h-4 w-4 mr-2" /> Edit
                  </Button>
                </DialogFooter>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={ticketDetailDialog} onOpenChange={(open) => {
        setTicketDetailDialog(open);
        if (open && selectedTicket) {
          fetchItemComments('tickets', selectedTicket.id);
        } else {
          setItemComments([]);
          setNewComment('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TicketIcon className="h-5 w-5" />
              {editMode ? 'Edit Ticket' : 'Ticket Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            editMode ? (
              <form onSubmit={handleUpdateTicket} className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm(p => ({ ...p, subject: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm(p => ({ ...p, description: e.target.value }))}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={ticketForm.type} onValueChange={(v) => setTicketForm(p => ({ ...p, type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="support">Support</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="feature_request">Feature Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm(p => ({ ...p, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={ticketForm.status} onValueChange={(v) => setTicketForm(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign To</Label>
                    <Select value={ticketForm.assigned_to || ''} onValueChange={(v) => setTicketForm(p => ({ ...p, assigned_to: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                      <SelectContent>
                        {teamUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      selectedTicket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                      selectedTicket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                      selectedTicket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-600'
                    }>{selectedTicket.status}</Badge>
                    <Badge variant="outline">{selectedTicket.priority}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Type: {selectedTicket.type}</span>
                  <span>Created: {format(new Date(selectedTicket.created_at), 'PPp')}</span>
                </div>
                
                {/* Original Description */}
                {selectedTicket.description && (
                  <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Original Issue</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>
                )}

                <Separator />

                {/* Comments Thread */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Updates & Notes ({itemComments.length})
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowFollowupForm(!showFollowupForm)}
                    >
                      <Users className="h-4 w-4 mr-1" /> Assign Follow-up
                    </Button>
                  </div>

                  {/* Follow-up Form */}
                  {showFollowupForm && (
                    <div className="p-3 bg-purple-50 rounded-lg mb-3 space-y-2">
                      <Label className="text-xs">Assign to team member</Label>
                      <Select value={followupAssignee} onValueChange={setFollowupAssignee}>
                        <SelectTrigger className="h-8"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                        <SelectContent>
                          {teamUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="Follow-up instructions..."
                        value={followupMessage}
                        onChange={(e) => setFollowupMessage(e.target.value)}
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleAddFollowup('tickets', selectedTicket.id)}>
                          Assign
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowFollowupForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Comments List */}
                  <ScrollArea className="h-[200px] pr-2">
                    <div className="space-y-3">
                      {loadingComments ? (
                        <p className="text-center text-sm text-muted-foreground py-4">Loading...</p>
                      ) : itemComments.length > 0 ? (
                        itemComments.map((comment, idx) => (
                          <div 
                            key={comment.id || idx} 
                            className={`p-3 rounded-lg ${comment.is_internal ? 'bg-amber-50 border-l-4 border-amber-400' : 'bg-slate-50'}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="text-xs bg-slate-200">
                                  {comment.author?.full_name?.[0] || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm">
                                {comment.author?.full_name || comment.author?.email || 'Unknown'}
                              </span>
                              {comment.is_internal && (
                                <Badge variant="outline" className="text-xs bg-amber-100">Internal</Badge>
                              )}
                              <span className="text-xs text-muted-foreground ml-auto">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm pl-8">{comment.content}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-sm text-muted-foreground py-4">No updates yet</p>
                      )}
                    </div>
                  </ScrollArea>

                  {/* Add Comment Form */}
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Add an update or note..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-muted-foreground">Internal note (hidden from customer)</span>
                      </label>
                      <Button size="sm" onClick={() => handleAddComment('tickets', selectedTicket.id)} disabled={!newComment.trim()}>
                        <Plus className="h-4 w-4 mr-1" /> Add Note
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {selectedTicket.status !== 'closed' ? (
                      <Button variant="outline" size="sm" className="text-emerald-600" onClick={() => handleCloseItem('tickets', selectedTicket.id)}>
                        <CheckSquare className="h-4 w-4 mr-1" /> Close Ticket
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => handleReopenItem('tickets', selectedTicket.id)}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Reopen
                      </Button>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => setEditMode(true)}>
                    <Edit className="h-4 w-4 mr-2" /> Edit
                  </Button>
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Deal Detail Dialog - Extracted */}
      <DealDetailDialog
        open={dealDetailDialog}
        onOpenChange={(open) => { setDealDetailDialog(open); if (!open) setEditMode(false); }}
        deal={selectedDeal}
        onUpdate={handleUpdateDeal}
        formatCurrency={formatCurrency}
      />

      {/* Subscription Detail Dialog - Extracted */}
      <SubscriptionDetailDialog
        open={subscriptionDetailDialog}
        onOpenChange={(open) => { setSubscriptionDetailDialog(open); if (!open) setEditMode(false); }}
        subscription={selectedSubscription}
        onUpdate={handleUpdateSubscription}
        formatCurrency={formatCurrency}
      />

      {/* Email Detail Dialog - Extracted */}
      <EmailDetailDialog
        open={emailDetailDialog}
        onOpenChange={setEmailDetailDialog}
        email={selectedEmail}
        onCreateTask={(entity) => { setQuickTaskEntity(entity); setQuickTaskDialog(true); }}
      />

      {/* Call Detail Dialog - Extracted */}
      <CallDetailDialog
        open={callDetailDialog}
        onOpenChange={setCallDetailDialog}
        call={selectedCall}
        onCreateTask={(entity) => { setQuickTaskEntity(entity); setQuickTaskDialog(true); }}
      />

      {/* Meeting Detail Dialog - Extracted */}
      <MeetingDetailDialog
        open={meetingDetailDialog}
        onOpenChange={(open) => { setMeetingDetailDialog(open); if (!open) setEditMode(false); }}
        meeting={selectedMeeting}
        onUpdate={handleUpdateMeeting}
      />

      {/* QuickTaskDialog - for creating tasks from emails, calls, meetings */}
      <QuickTaskDialog
        open={quickTaskDialog}
        onOpenChange={setQuickTaskDialog}
        relatedToType={quickTaskEntity.type}
        relatedToId={quickTaskEntity.id}
        relatedToName={quickTaskEntity.name}
        onTaskCreated={() => fetchTabData()}
      />

      {/* Payment Detail Dialog - Extracted */}
      <PaymentDetailDialog
        open={paymentDetailDialog}
        onOpenChange={setPaymentDetailDialog}
        payment={selectedPayment}
        formatCurrency={formatCurrency}
      />

      {/* Add Contact Product Dialog */}
      <Dialog open={contactProductDialog} onOpenChange={setContactProductDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Assign Product to Contact
            </DialogTitle>
            <DialogDescription>
              Add a product this customer owns with serial number and pricing details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddContactProduct} className="space-y-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select 
                value={contactProductForm.product_id} 
                onValueChange={(v) => {
                  const selectedProduct = products.find(p => p.id === v);
                  setContactProductForm(p => ({ 
                    ...p, 
                    product_id: v,
                    purchase_price: selectedProduct?.price?.toString() || ''
                  }));
                }}
              >
                <SelectTrigger data-testid="contact-product-select">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <span>{product.name}</span>
                        <span className="text-muted-foreground">({product.sku})</span>
                        <span className="font-medium">{formatCurrency(product.price)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contactProductForm.product_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  {(() => {
                    const selectedProduct = products.find(p => p.id === contactProductForm.product_id);
                    return selectedProduct ? (
                      <div className="text-sm">
                        <p className="font-medium text-blue-900">{selectedProduct.name}</p>
                        <p className="text-blue-700">SKU: {selectedProduct.sku}</p>
                        <p className="text-blue-700">Category: {selectedProduct.category}</p>
                        <p className="text-blue-700">List Price: {formatCurrency(selectedProduct.price)}</p>
                        {selectedProduct.warranty_period_months && (
                          <p className="text-blue-700">Warranty: {selectedProduct.warranty_period_months} months</p>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Serial Numbers</Label>
              <p className="text-xs text-muted-foreground">Add one or more serial numbers for this product</p>
              <div className="space-y-2">
                {contactProductForm.serial_numbers.map((sn, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={sn}
                      onChange={(e) => {
                        const updated = [...contactProductForm.serial_numbers];
                        updated[idx] = e.target.value;
                        setContactProductForm(p => ({ ...p, serial_numbers: updated }));
                      }}
                      placeholder={`Serial number ${idx + 1}`}
                      data-testid={`contact-product-serial-${idx}`}
                    />
                    {contactProductForm.serial_numbers.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          const updated = contactProductForm.serial_numbers.filter((_, i) => i !== idx);
                          setContactProductForm(p => ({ ...p, serial_numbers: updated }));
                        }}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setContactProductForm(p => ({ ...p, serial_numbers: [...p.serial_numbers, ''] }))}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Serial Number
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={contactProductForm.purchase_date}
                  onChange={(e) => setContactProductForm(p => ({ ...p, purchase_date: e.target.value }))}
                  data-testid="contact-product-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Purchase Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={contactProductForm.purchase_price}
                  onChange={(e) => setContactProductForm(p => ({ ...p, purchase_price: e.target.value }))}
                  placeholder="Override price"
                  data-testid="contact-product-price"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={contactProductForm.discount_percent}
                  onChange={(e) => setContactProductForm(p => ({ ...p, discount_percent: e.target.value }))}
                  placeholder="0"
                  data-testid="contact-product-discount-pct"
                />
              </div>
              <div className="space-y-2">
                <Label>Discount Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={contactProductForm.discount_amount}
                  onChange={(e) => setContactProductForm(p => ({ ...p, discount_amount: e.target.value }))}
                  placeholder="0"
                  data-testid="contact-product-discount-amt"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={contactProductForm.notes}
                onChange={(e) => setContactProductForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any special notes..."
                rows={2}
                data-testid="contact-product-notes"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!contactProductForm.product_id} data-testid="contact-product-submit">
                <Package className="h-4 w-4 mr-2" />
                Assign Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Product Detail Dialog */}
      <Dialog open={contactProductDetailDialog} onOpenChange={setContactProductDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Product Details
            </DialogTitle>
          </DialogHeader>
          {selectedContactProduct && (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{selectedContactProduct.product?.name}</h3>
                  <p className="text-sm text-muted-foreground">SKU: {selectedContactProduct.product?.sku}</p>
                  <p className="text-sm text-muted-foreground">Category: {selectedContactProduct.product?.category || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input
                    value={selectedContactProduct.serial_number || ''}
                    onChange={(e) => setSelectedContactProduct(p => ({ ...p, serial_number: e.target.value }))}
                    placeholder="Not set"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={selectedContactProduct.status} 
                    onValueChange={(v) => setSelectedContactProduct(p => ({ ...p, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="returned">Returned</SelectItem>
                      <SelectItem value="exchanged">Exchanged</SelectItem>
                      <SelectItem value="warranty_claim">Warranty Claim</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Purchase Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedContactProduct.purchase_price || ''}
                    onChange={(e) => setSelectedContactProduct(p => ({ ...p, purchase_price: e.target.value }))}
                    placeholder="Not set"
                  />
                </div>
                <div className="space-y-2">
                  <Label>List Price</Label>
                  <Input
                    value={formatCurrency(selectedContactProduct.product?.price || 0)}
                    disabled
                    className="bg-slate-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={selectedContactProduct.discount_percent || 0}
                    onChange={(e) => setSelectedContactProduct(p => ({ ...p, discount_percent: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedContactProduct.discount_amount || 0}
                    onChange={(e) => setSelectedContactProduct(p => ({ ...p, discount_amount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={selectedContactProduct.notes || ''}
                  onChange={(e) => setSelectedContactProduct(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                />
              </div>

              {selectedContactProduct.purchase_date && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <span className="text-muted-foreground">Purchased:</span>{' '}
                  {format(new Date(selectedContactProduct.purchase_date), 'PPP')}
                </div>
              )}

              {selectedContactProduct.warranty_expiry && (
                <div className={`p-3 rounded-lg text-sm ${
                  new Date(selectedContactProduct.warranty_expiry) > new Date() ? 'bg-emerald-50' : 'bg-red-50'
                }`}>
                  <span className="text-muted-foreground">Warranty:</span>{' '}
                  {new Date(selectedContactProduct.warranty_expiry) > new Date() ? 'Valid' : 'Expired'} until{' '}
                  {format(new Date(selectedContactProduct.warranty_expiry), 'PPP')}
                </div>
              )}

              <DialogFooter className="flex gap-2">
                <Button 
                  variant="destructive" 
                  onClick={() => handleDeleteContactProduct(selectedContactProduct.id)}
                  data-testid="delete-contact-product-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
                <Button onClick={handleUpdateContactProduct} data-testid="update-contact-product-btn">
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Portal Access Dialog */}
      <PortalAccessDialog
        open={portalAccessDialog}
        onOpenChange={setPortalAccessDialog}
        contactId={contactId}
        contact={contact}
      />

      {/* Segments Management Dialog */}
      <SegmentsDialog
        open={segmentsDialog}
        onOpenChange={setSegmentsDialog}
        contactId={contactId}
        initialSegments={contact?.segments || []}
        onSuccess={() => fetchContactData()}
      />

      {/* Edit Contact Dialog */}
      <EditContactDialog
        open={editContactDialog}
        onOpenChange={setEditContactDialog}
        contactId={contactId}
        contact={contact}
        onSuccess={() => fetchContactData()}
      />

      {/* Customer Status Dialog */}
      <CustomerStatusDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        contactId={contactId}
        currentStatus={contact?.customer_status}
        onSuccess={() => fetchContactData()}
      />

      {/* Inline Create Ticket Dialog */}
      <InlineTicketDialog
        open={showInlineTicketDialog}
        onOpenChange={setShowInlineTicketDialog}
        contactId={contactId}
        contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`}
        teamMembers={teamUsers}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      <InlineDealDialog
        open={showInlineDealDialog}
        onOpenChange={setShowInlineDealDialog}
        contactId={contactId}
        contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      <InlinePaymentDialog
        open={showInlinePaymentDialog}
        onOpenChange={setShowInlinePaymentDialog}
        contactId={contactId}
        contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />

      <InlineSubscriptionDialog
        open={showInlineSubscriptionDialog}
        onOpenChange={setShowInlineSubscriptionDialog}
        contactId={contactId}
        contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`}
        onSuccess={() => { fetchTabData(); fetchContactData(); }}
      />
    </div>
  );
};

export default ContactProfilePage;
